import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Spin, notification, Modal } from 'antd';
import {
  AppstoreOutlined, BankOutlined, BellFilled, BuildOutlined, BulbOutlined, CameraOutlined, CheckCircleOutlined,
  CompassOutlined, CrownOutlined, DeleteOutlined, EditOutlined, EnvironmentOutlined,
  EyeOutlined, GlobalOutlined, LinkOutlined, LikeOutlined, LoadingOutlined, MailOutlined,
  MessageOutlined, NotificationOutlined, PhoneOutlined, PlusOutlined, RightOutlined,
  HeartOutlined, ReadOutlined, RiseOutlined, SearchOutlined, ShopOutlined,
  SoundOutlined, TeamOutlined, TrophyOutlined, UserAddOutlined, UserOutlined, UsergroupAddOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl, rozerpay_key_id } from '../../../config';
import { M, G, BG } from '../shared/constants';
import { authHeader, useLang, ssGet, ssSet, ssInvalidate, TODAY, countryFlag, fmtAmount, toTitle } from '../shared/utils';
import { PostCard, PostDetailModal, InlineCommentSection, WritePostModal, CAT_META, timeAgo } from '../components/PostCard';
import { NewsDetailModal } from '../components/NewsDetailModal';
import { MemberProfileModal } from '../components/MemberProfileModal';
import { fmtEventDate, PLATFORM_META } from './Village';

export function getTodayPanchang() {
  const now  = new Date();
  const days = ['रविवार','सोमवार','मंगलवार','बुधवार','गुरुवार','शुक्रवार','शनिवार'];
  const vMonths = ['चैत्र','वैशाख','ज्येष्ठ','आषाढ़','श्रावण','भाद्रपद','आश्विन','कार्तिक','मार्गशीर्ष','पौष','माघ','फाल्गुन'];
  // Approximate Vikram Samvat (Gregorian + 56 to 57 depending on month)
  const vs = now.getMonth() < 3 || (now.getMonth() === 3 && now.getDate() < 14)
    ? now.getFullYear() + 56 : now.getFullYear() + 57;
  // Approximate tithi from lunar cycle (~29.53 days)
  const knownNewMoon = new Date('2024-01-11').getTime();
  const lunarDay = Math.floor(((now.getTime() - knownNewMoon) % (29.53 * 86400000)) / 86400000);
  const tithi    = (lunarDay % 15) + 1;
  const paksha   = lunarDay < 15 ? 'शुक्ल पक्ष' : 'कृष्ण पक्ष';
  const tithiNames = ['प्रतिपदा','द्वितीया','तृतीया','चतुर्थी','पंचमी','षष्ठी','सप्तमी','अष्टमी','नवमी','दशमी','एकादशी','द्वादशी','त्रयोदशी','चतुर्दशी','पूर्णिमा/अमावस्या'];
  // Approximate Hindu month from Gregorian month
  const hMonthIndex = ((now.getMonth() - 3) + 12) % 12;
  return {
    vaara:   days[now.getDay()],
    paksha,
    tithi:   tithiNames[Math.min(tithi - 1, 14)],
    masa:    vMonths[hMonthIndex],
    samvat:  vs,
  };
}

// ── Profile Tab ───────────────────────────────────────────────────────────────
// ── Home Dashboard Tab ────────────────────────────────────────────────────────
export function HomeDashboardTab({ profile, onSwitchTab, tabBadges = {}, onlineUsers = [], onChatOpen, onQuickPay }) {
  const { t } = useLang();
  const [dash,           setDash]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [countryStats,   setCountryStats]   = useState(null);
  const [countryExpanded,setCountryExpanded]= useState(false);
  const [gramPramukh,    setGramPramukh]    = useState(undefined);
  const [nextEvent,      setNextEvent]      = useState(null);
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [missionJoining,      setMissionJoining]      = useState(false);
  const [missionMsg,          setMissionMsg]          = useState(null);
  const [missionAutoPayOffer, setMissionAutoPayOffer] = useState(false);
  const [missionAutoPayLoading, setMissionAutoPayLoading] = useState(false);
  const [openedPost,     setOpenedPost]     = useState(null);
  const [openedNews,     setOpenedNews]     = useState(null);
  const [payPrompt,      setPayPrompt]      = useState(false);
  const [dailyQuote,     setDailyQuote]     = useState(null);
  const [leaderboard,    setLeaderboard]    = useState([]);
  const [writeOpen,      setWriteOpen]      = useState(false);
  const [posts,          setPosts]          = useState([]);
  const [postsPage,      setPostsPage]      = useState(1);
  const [postsTotal,     setPostsTotal]     = useState(0);
  const [myPosts,        setMyPosts]        = useState(null); // null = loading, [] = no posts
  const [featuredBizOpen,   setFeaturedBizOpen]   = useState(false);
  const [featuredBizStatus, setFeaturedBizStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [postsLoading,   setPostsLoading]   = useState(false);
  const [expandedPost,   setExpandedPost]   = useState(null); // post id with comments open
  const [feedOpenPost,   setFeedOpenPost]   = useState(null); // full modal for feed post
  // ── Engagement Features ──
  const [checkin,        setCheckin]        = useState(null); // { checked_today, streak, total }
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [stories,        setStories]        = useState([]); // grouped by user
  const [storyViewer,    setStoryViewer]    = useState(null); // { userIdx, storyIdx }
  const [competition,    setCompetition]    = useState([]);
  const [milestones,     setMilestones]     = useState([]);
  const [adminPhotos,    setAdminPhotos]    = useState([]); // admin 24hr status photos
  const [adminPhotoView, setAdminPhotoView] = useState(null); // { idx }
  const storyFileRef = useRef(null);
  // Caption modal for story upload
  const [captionModal,   setCaptionModal]   = useState(false);
  const [pendingStoryUrl,setPendingStoryUrl]= useState('');
  // 🙏 आज के समर्पण story
  const [todayPays,      setTodayPays]      = useState([]);
  const [samarpanViewer, setSamarpanViewer] = useState(null); // index into todayPays
  const [samarpanThanked,setSamarpanThanked]= useState(new Set());
  const [samarpanConnected,setSamarpanConnected]=useState(new Set());
  const [captionText,    setCaptionText]    = useState('');
  // One-time payment change request
  const [oneTimeReqModal,  setOneTimeReqModal]  = useState(false);
  const [oneTimeReqSent,   setOneTimeReqSent]   = useState(false);
  const [oneTimeReqSending,setOneTimeReqSending]= useState(false);
  const [captionSaving,  setCaptionSaving]  = useState(false);
  // Check-in welcome popup
  const [checkinPopup,   setCheckinPopup]   = useState(false); // show on first visit if not checked in
  const [checkinDone,    setCheckinDone]    = useState(false); // animation after check-in
  // Scholarship impact stats
  const [scholarshipImpact, setScholarshipImpact] = useState(null);
  // Member profile modal (from online strip / suggestions)
  const [onlineProfileId,   setOnlineProfileId]   = useState(null);

  useEffect(() => {
    // Daily quote — ?d=TODAY makes each day a unique URL so yesterday's browser HTTP cache (max-age=43200) never serves stale data
    const CK_QUOTE = `dq_${TODAY}`;
    const cachedQuote = ssGet(CK_QUOTE, 12 * 60 * 60 * 1000);
    if (cachedQuote) { setDailyQuote(cachedQuote); }
    else {
      axios.get(`${baseUrl}/member/daily-quote?d=${TODAY}`, { headers: authHeader() })
        .then(r => { if (r.data.status && r.data.result) { setDailyQuote(r.data.result); ssSet(CK_QUOTE, r.data.result); } })
        .catch(() => {});
    }
    // Village leaderboard — cache 30 min (server also sends Cache-Control: private, max-age=1800)
    const CK_LB = 'village_lb';
    const cachedLb = ssGet(CK_LB, 30 * 60 * 1000);
    if (cachedLb) { setLeaderboard(cachedLb); }
    else {
      axios.get(`${baseUrl}/member/village-leaderboard`, { headers: authHeader() })
        .then(r => { if (r.data.status) { setLeaderboard(r.data.result || []); ssSet(CK_LB, r.data.result || []); } })
        .catch(() => {});
    }
    // Next upcoming event (from cache if available)
    const CK_EV = 'sammelan_events';
    const cachedEv = ssGet(CK_EV, 5 * 60 * 1000);
    if (cachedEv?.upcoming?.[0]) { setNextEvent(cachedEv.upcoming[0]); }
    else {
      axios.get(`${baseUrl}/member/sammelan`, { headers: authHeader() })
        .then(r => {
          if (r.data.status) {
            ssSet(CK_EV, { upcoming: r.data.upcoming, past: r.data.past });
            setNextEvent(r.data.upcoming?.[0] || null);
          }
        }).catch(() => {});
    }

    // Gram Pramukh for member's own village (only if they have a permanent village)
    const myVillageHn = profile?.perm_address?.village;
    if (myVillageHn) {
      axios.get(`${baseUrl}/member/gram-pramukh?village_name_hn=${encodeURIComponent(myVillageHn)}`, { headers: authHeader() })
        .then(r => setGramPramukh(r.data.result || null))
        .catch(() => setGramPramukh(null));
    } else { setGramPramukh(null); }

    // Country stats — cache 30 min
    const CK_CS = 'country_stats';
    const cachedCs = ssGet(CK_CS, 30 * 60 * 1000);
    if (cachedCs) { setCountryStats(cachedCs); }
    else {
      axios.get(`${baseUrl}/member/country-stats`, { headers: authHeader() })
        .then(r => { if (r.data.status) { setCountryStats(r.data); ssSet(CK_CS, r.data); } })
        .catch(() => {});
    }
    // My posts activity — show engagement card on home
    axios.get(`${baseUrl}/member/posts/mine`, { headers: authHeader() })
      .then(r => { if (r.data.status) setMyPosts(r.data.result || []); else setMyPosts([]); })
      .catch(() => setMyPosts([]));
    // Daily check-in streak — show welcome popup if not checked in yet today
    axios.get(`${baseUrl}/member/checkin/status`, { headers: authHeader() })
      .then(r => {
        if (r.data.status) {
          setCheckin(r.data);
          if (!r.data.checked_today && !sessionStorage.getItem('checkinPopupSeen')) {
            setTimeout(() => setCheckinPopup(true), 1500); // slight delay so page settles
          }
        }
      }).catch(() => {});
    // Photo stories
    axios.get(`${baseUrl}/member/stories`, { headers: authHeader() })
      .then(r => { if (r.data.status) setStories(r.data.result || []); }).catch(() => {});
    // 🙏 आज के समर्पण
    axios.get(`${baseUrl}/member/today-payments`, { headers: authHeader() })
      .then(r => { if (r.data.status) setTodayPays(r.data.result || []); }).catch(() => {});
    // Village competition
    axios.get(`${baseUrl}/member/village-competition`, { headers: authHeader() })
      .then(r => { if (r.data.status) setCompetition(r.data.result || []); }).catch(() => {});
    // Member milestones
    axios.get(`${baseUrl}/member/milestones/today`, { headers: authHeader() })
      .then(r => { if (r.data.status) setMilestones(r.data.result || []); }).catch(() => {});
    // Admin 24hr status photos
    axios.get(`${baseUrl}/member/status-photos`, { headers: authHeader() })
      .then(r => { if (r.data.status) setAdminPhotos(r.data.result || []); }).catch(() => {});
    // Scholarship impact — summary stats + latest 5
    axios.get(`${baseUrl}/member/scholarships`, { headers: authHeader() })
      .then(r => {
        if (r.data.status && r.data.total > 0) {
          setScholarshipImpact({ total: r.data.total, boys: r.data.boys, girls: r.data.girls, years: r.data.years || [], latest: r.data.latest || [] });
        }
      }).catch(() => {});
  }, []);

  useEffect(() => {
    // Home dashboard — cache 2 min. Version key forces re-fetch after new fields added.
    const CK_DASH = 'home_dash_v2';
    const cachedDash = ssGet(CK_DASH, 2 * 60 * 1000);
    if (cachedDash) {
      setDash(cachedDash);
      const key = 'payPromptShown_' + new Date().getMonth();
      if (!cachedDash.this_month_paid && cachedDash.payment_commitment !== 'one_time' && !sessionStorage.getItem(key))
        setTimeout(() => setPayPrompt(true), 1200);
      setLoading(false);
    } else {
      axios.get(`${baseUrl}/member/home-dashboard`, { headers: authHeader() })
        .then(r => {
          if (r.data.status) {
            setDash(r.data);
            ssSet('home_dash_v2', r.data);
            const key = 'payPromptShown_' + new Date().getMonth();
            if (!r.data.this_month_paid && r.data.payment_commitment !== 'one_time' && !sessionStorage.getItem(key))
              setTimeout(() => setPayPrompt(true), 1200);
          }
        })
        .catch(() => {}).finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line

  const fetchPosts = useCallback(async (page = 1) => {
    setPostsLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/posts?page=${page}&limit=10`, { headers: authHeader() });
      if (r.data.status) {
        setPosts(p => page === 1 ? (r.data.result || []) : [...p, ...(r.data.result || [])]);
        setPostsTotal(r.data.total || 0);
        setPostsPage(page);
      }
    } catch {} finally { setPostsLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchPosts(1); }, []); // eslint-disable-line

  const handleReact = async (postId, reaction) => {
    try {
      const r = await axios.post(`${baseUrl}/member/posts/${postId}/react`, { reaction }, { headers: authHeader() });
      if (r.data.status) {
        setPosts(prev => prev.map(p => p.id === postId ? {
          ...p,
          my_reaction:   r.data.removed ? null : reaction,
          reaction_count: r.data.removed ? (p.reaction_count - 1) : (p.my_reaction ? p.reaction_count : p.reaction_count + 1),
        } : p));
      }
    } catch {}
  };

  const dismissPayPrompt = () => {
    const key = 'payPromptShown_' + new Date().getMonth();
    sessionStorage.setItem(key, '1');
    setPayPrompt(false);
  };

  const goPayNow = () => {
    dismissPayPrompt();
    onSwitchTab('payments');
  };

  const refreshDash = async () => {
    ssInvalidate('home_dash_v2');
    const fresh = await axios.get(`${baseUrl}/member/home-dashboard`, { headers: authHeader() });
    if (fresh.data.status) { setDash(fresh.data); ssSet('home_dash_v2', fresh.data); }
  };

  const handleJoinMission21K = async () => {
    setMissionJoining(true);
    setMissionMsg(null);
    try {
      const r = await axios.post(`${baseUrl}/member/mission-21k/join`, {}, { headers: authHeader() });

      // Already enrolled or high-amount member → just show success
      if (r.data.already_enrolled || !r.data.payment_required) {
        setMissionMsg({ ok: true, text: r.data.message });
        await refreshDash();
        return;
      }

      // Payment required → open Razorpay for ₹200
      const { order_id, amount, name, phone, bank_account_id } = r.data;
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = resolve; script.onerror = reject;
        document.body.appendChild(script);
      });

      const rzp = new window.Razorpay({
        key:         rozerpay_key_id,
        amount:      amount * 100,
        currency:    'INR',
        name:        'ABRSVS — Mission 21K',
        description: '₹200/माह Mission 21K पहला समर्पण',
        order_id,
        prefill:     { name, contact: phone },
        theme:       { color: '#b45309' },
        redirect:    true,
        callback_url: `${window.location.origin}/user-portal?payment_return=1`,
        handler: async (response) => {
          try {
            const vRes = await axios.post(`${baseUrl}/member/mission-21k/verify-payment`, {
              order_id:       response.razorpay_order_id,
              payment_id:     response.razorpay_payment_id,
              signature:      response.razorpay_signature,
              bank_account_id,
            }, { headers: authHeader() });
            setMissionMsg({ ok: vRes.data.status, text: vRes.data.message });
            if (vRes.data.status) setMissionAutoPayOffer(true);
            await refreshDash();
          } catch {
            setMissionMsg({ ok: false, text: 'Payment verify नहीं हो सका, office से संपर्क करें।' });
          } finally { setMissionJoining(false); }
        },
        modal: { ondismiss: () => { setMissionMsg({ ok: false, text: 'Payment cancelled।' }); setMissionJoining(false); } },
      });
      rzp.open();
      return; // don't setMissionJoining(false) here — handler does it
    } catch (e) {
      setMissionMsg({ ok: false, text: e?.response?.data?.message || 'कुछ गड़बड़ हुई, पुनः प्रयास करें' });
    } finally {
      // Only reset if not waiting for Razorpay modal
      if (!window.Razorpay) setMissionJoining(false);
    }
  };

  const handleEnableAutoPayFromMission = async () => {
    setMissionAutoPayLoading(true);
    try {
      const loaded = await new Promise(resolve => {
        if (window.Razorpay) { resolve(true); return; }
        const s = document.createElement('script');
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.onload = () => resolve(true); s.onerror = () => resolve(false);
        document.body.appendChild(s);
      });
      if (!loaded) { notification.error({ message: 'Razorpay SDK load failed.' }); return; }

      const res = await axios.post(`${baseUrl}/member/razorpay/create-subscription`, {}, { headers: authHeader() });
      if (!res.data.subscription_id) throw new Error(res.data.message || 'Subscription creation failed');

      const rzp = new window.Razorpay({
        key:             rozerpay_key_id,
        subscription_id: res.data.subscription_id,
        name:            'समाज शिक्षा निधि',
        description:     'Auto-pay ₹200/माह — Mission 21K समर्पण',
        prefill:         { name: res.data.name, contact: res.data.phone },
        theme:           { color: '#b45309' },
        handler: () => {
          setMissionAutoPayOffer(false);
          notification.success({
            message: '✅ Auto-pay चालू हो गया!',
            description: 'अब हर माह ₹200 automatically कट जाएगा।',
            duration: 6, placement: 'topRight',
          });
        },
        modal: { ondismiss: () => setMissionAutoPayLoading(false) },
      });
      rzp.open();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || err.message || 'Auto-pay setup failed', placement: 'topRight' });
      setMissionAutoPayLoading(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
  if (!dash)   return null;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'शुभ प्रभात' : hour < 17 ? 'नमस्ते' : 'शुभ संध्या';
  const firstName = (dash.name || '').split(' ')[0];

  const BADGE = {
    gold:   { label: '🥇 Gold Donor',   color: '#92400e', bg: '#fef3c7', border: '#fbbf24' },
    silver: { label: '🥈 Silver Donor', color: '#374151', bg: '#f3f4f6', border: '#9ca3af' },
    bronze: { label: '🥉 Bronze Donor', color: '#7c2d12', bg: '#ffedd5', border: '#fb923c' },
  };
  const badge = dash.badge ? BADGE[dash.badge] : null;

  const ini = n => (n || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div>

      {/* ── TODAY'S HIGHLIGHTS STRIP — always visible ── */}
      {(() => {
        const chips = [
          tabBadges.contacts > 0 && { label: `${tabBadges.contacts} नए अनुरोध`, icon: <MailOutlined />,    tab: 'contacts', bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8' },
          tabBadges.chat     > 0 && { label: `${tabBadges.chat} संदेश`,          icon: <MessageOutlined />, tab: 'chat',     bg: '#f0fdf4', border: '#86efac', color: '#15803d' },
          tabBadges.board    > 0 && { label: `${tabBadges.board} नई पोस्ट`,      icon: <CrownOutlined />,   tab: 'board',    bg: '#faf5ff', border: '#c4b5fd', color: '#7c3aed' },
        ].filter(Boolean);
        return (
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 12, scrollbarWidth: 'none', paddingBottom: 2, alignItems: 'center' }}>
            {chips.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 20, padding: '5px 13px' }}>
                <CheckCircleOutlined style={{ fontSize: 13, color: '#15803d' }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', whiteSpace: 'nowrap' }}>सब अपडेट है — कोई नया notification नहीं</span>
              </div>
            ) : chips.map(c => (
              <div key={c.tab} onClick={() => onSwitchTab(c.tab)} style={{
                flexShrink: 0, background: c.bg, border: `1.5px solid ${c.border}`,
                borderRadius: 20, padding: '5px 13px',
                display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                <span style={{ fontSize: 13, color: c.color }}>{c.icon}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: c.color, whiteSpace: 'nowrap' }}>{c.label}</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Online Members strip ── */}
      {onlineUsers.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>अभी Online · {onlineUsers.length} सदस्य</span>
            </div>
            <span style={{ fontSize: 10, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
              <MessageOutlined style={{ fontSize: 10, color: '#15803d' }} /> tap = profile · chat
            </span>
          </div>
          <div style={{
            display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none',
            paddingBottom: 4,
          }}>
            {onlineUsers.map(u => (
              <div key={u.id} style={{
                flexShrink: 0, width: 160,
                background: '#fff', border: '1px solid #bbf7d0',
                borderRadius: 12, padding: '10px 12px',
                display: 'flex', alignItems: 'center', gap: 10,
                boxShadow: '0 1px 6px rgba(34,197,94,0.1)',
              }}>
                {/* Avatar with online dot */}
                <div style={{ position: 'relative', flexShrink: 0, cursor: 'pointer' }}
                  onClick={() => setOnlineProfileId(u.id)}>
                  {u.photo
                    ? <img src={u.photo} alt={u.name} onClick={() => setOnlineProfileId(u.id)}
                        style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top',
                          border: '2.5px solid #22c55e', cursor: 'pointer', display: 'block' }} />
                    : <div onClick={() => setOnlineProfileId(u.id)} style={{ width: 40, height: 40, borderRadius: '50%', background: M,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 800, color: G, border: '2.5px solid #22c55e', cursor: 'pointer' }}>
                        {ini(u.name)}
                      </div>
                  }
                  <span style={{ position: 'absolute', bottom: 1, right: 1, width: 10, height: 10,
                    borderRadius: '50%', background: '#22c55e', border: '2px solid #fff' }} />
                </div>
                {/* Name + village */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#111827', lineHeight: 1.3,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.name || '—'}
                  </div>
                  <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.3, marginTop: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[u.village, u.district].filter(Boolean).join(', ') || 'सदस्य'}
                  </div>
                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                    <button onClick={() => setOnlineProfileId(u.id)} style={{
                      flex: 1, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6,
                      padding: '3px 0', fontSize: 9, fontWeight: 700, color: '#15803d', cursor: 'pointer',
                    }}>
                      प्रोफ़ाइल
                    </button>
                    <button onClick={() => onChatOpen && onChatOpen(u.id)} style={{
                      flex: 1, background: M, border: 'none', borderRadius: 6,
                      padding: '3px 0', fontSize: 9, fontWeight: 700, color: G, cursor: 'pointer',
                    }}>
                      Chat
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile modal for online members */}
      {onlineProfileId && (
        <MemberProfileModal
          memberId={onlineProfileId}
          onClose={() => setOnlineProfileId(null)}
          onSwitchTab={onSwitchTab}
        />
      )}

      {/* ── HERO CARD — Greeting + Member info + Streak merged ── */}
      <div onClick={() => onSwitchTab('profile')} style={{
        background: `linear-gradient(135deg, ${M} 0%, #b71c1c 60%, #7a0000 100%)`,
        borderRadius: 16, padding: '16px 16px 14px', marginBottom: 12,
        color: '#fff', position: 'relative', overflow: 'hidden', cursor: 'pointer',
      }}>
        {/* Decorative circle */}
        <div style={{ position: 'absolute', top: -24, right: -24, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

        {/* Top row: portal label + photo */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
              ABRSVS सदस्य पोर्टल
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>
              {greeting}, {firstName} जी! 🙏
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 3 }}>
              {new Date().toLocaleDateString('hi-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          {/* User photo */}
          {profile && (
            <div style={{ flexShrink: 0, marginLeft: 12 }}>
              {profile.photo
                ? <img src={profile.photo} alt=""
                    style={{ width: 52, height: 52, borderRadius: 10, objectFit: 'cover', objectPosition: 'top',
                      border: '2.5px solid rgba(255,255,255,0.4)', display: 'block' }} />
                : <div style={{ width: 52, height: 52, borderRadius: 10, background: 'rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 900, color: G, border: '2px solid rgba(255,255,255,0.3)' }}>
                    {(profile.name_hn || profile.name || '?')[0]}
                  </div>
              }
            </div>
          )}
        </div>

        {/* Bottom row: ID + Active + Streak */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 10 }}>
          {/* ID + joining year */}
          {profile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 800, background: G, color: M, borderRadius: 6, padding: '2px 8px' }}>
                #{profile.membership_no || `ID-${profile.id}`}
              </span>
              {profile.joining_date && (
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                  सदस्य {new Date(profile.joining_date).getFullYear()} से
                </span>
              )}
            </div>
          )}
          {/* ACTIVE badge */}
          <span style={{ fontSize: 10, fontWeight: 800, background: '#15803d', color: '#fff', borderRadius: 20, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#86efac', display: 'inline-block' }} /> ACTIVE
          </span>
          {/* Streak */}
          {checkin?.checked_today && (
            <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
              <RiseOutlined style={{ fontSize: 10 }} />
              {checkin.streak} दिन streak
              <CheckCircleOutlined style={{ fontSize: 10, color: '#86efac' }} />
            </span>
          )}
          {/* Donor badge */}
          {badge && (
            <span style={{ fontSize: 10, fontWeight: 800, background: badge.bg, color: badge.color, borderRadius: 20, padding: '2px 8px', border: `1px solid ${badge.border}` }}>
              {badge.label}
            </span>
          )}
        </div>
      </div>

      {/* ── Photo Stories ── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', scrollbarWidth: 'none', padding: '4px 0' }}>
          {/* Add Story button — WhatsApp style */}
          {(() => {
            const myStory  = stories.find(u => u.user_id === profile?.id);
            const storyImg = myStory?.stories?.[myStory.stories.length - 1]?.photo_url;
            const hasStory = !!myStory;
            return (
              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                onClick={() => storyFileRef.current?.click()}>
                <div style={{ position: 'relative', width: 56, height: 56 }}>
                  {/* Ring — gradient if story exists, grey dashed if not */}
                  <div style={{ width: 56, height: 56, borderRadius: '50%', padding: hasStory ? 2 : 0,
                    background: hasStory ? `linear-gradient(135deg,${M},#FFD700)` : 'transparent',
                    border: hasStory ? 'none' : `2.5px dashed ${M}` }}>
                    {storyImg ? (
                      /* Show the actual story image */
                      <img src={storyImg} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', border: hasStory ? '2px solid #fff' : 'none' }} />
                    ) : (
                      /* No story yet — show camera icon + "जोड़ें" */
                      <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: '#fef2f2',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <CameraOutlined style={{ fontSize: 16, color: M }} />
                        <span style={{ fontSize: 7, fontWeight: 800, color: M }}>जोड़ें</span>
                      </div>
                    )}
                  </div>
                  {/* + badge bottom-right */}
                  <div style={{ position: 'absolute', bottom: 0, right: 0, width: 18, height: 18, borderRadius: '50%',
                    background: M, border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                    <span style={{ fontSize: 13, fontWeight: 900, color: '#fff', lineHeight: 1 }}>+</span>
                  </div>
                </div>
                <div style={{ fontSize: 9, color: M, fontWeight: 700, textAlign: 'center', width: 56 }}>
                  {hasStory ? 'मेरी Story' : 'Story जोड़ें'}
                </div>
              </div>
            );
          })()}
          {/* 🙏 आज के समर्पण bubble — rectangular like app */}
          {todayPays.length > 0 && (() => {
            const first = todayPays[0];
            const ini = (n) => (n||'?').trim().split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
            return (
              <div onClick={() => { setSamarpanViewer(0); setSamarpanThanked(new Set()); setSamarpanConnected(new Set()); }}
                style={{ flexShrink: 0, cursor: 'pointer' }}>
                <div style={{
                  position: 'relative', width: 92, height: 140, borderRadius: 14,
                  border: `2.5px solid ${G}`, overflow: 'hidden', backgroundColor: M,
                  boxShadow: `0 4px 14px rgba(122,0,0,0.35)`,
                }}>
                  {first.photo
                    ? <img src={first.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 22, fontWeight: 900, color: G }}>{ini(first.name)}</div>}
                  {/* Shade overlay */}
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(122,0,0,0.25)' }} />
                  {/* Count badge */}
                  <div style={{ position: 'absolute', top: 6, left: 6, background: G, borderRadius: 12,
                    padding: '2px 7px', fontSize: 11, fontWeight: 900, color: M }}>
                    🙏 {todayPays.length}
                  </div>
                  {/* Footer */}
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'rgba(122,0,0,0.88)', padding: '5px 7px' }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: G, lineHeight: 1.3 }}>आज के समर्पण</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.9)', lineHeight: 1.3,
                      overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                      {first.name?.split(' ')[0]}{todayPays.length > 1 ? ` +${todayPays.length - 1}` : ''}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          <input ref={storyFileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const fd = new FormData();
              fd.append('file', file);
              try {
                const up = await axios.post(`${baseUrl}/upload/story`, fd, { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } });
                if (up.data.fileUrl) {
                  setPendingStoryUrl(up.data.fileUrl);
                  setCaptionText('');
                  setCaptionModal(true);
                }
              } catch { notification.error({ message: 'Story upload failed', placement: 'topRight' }); }
              e.target.value = '';
            }}
          />
          {/* Story rings — show story image, not profile photo */}
          {stories.map((u, uIdx) => {
            const latestStoryImg = u.stories?.[u.stories.length - 1]?.photo_url;
            return (
              <div key={u.user_id} style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                onClick={() => setStoryViewer({ userIdx: uIdx, storyIdx: 0 })}>
                <div style={{ position: 'relative', width: 56, height: 56 }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', padding: 2,
                    background: `linear-gradient(135deg,${M},#FFD700)`, flexShrink: 0 }}>
                    {latestStoryImg ? (
                      <img src={latestStoryImg} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', border: '2px solid #fff' }} />
                    ) : u.photo ? (
                      <img src={u.photo} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: '2px solid #fff' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: M, border: '2px solid #fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#FFD700' }}>
                        {(u.name||'?')[0]}
                      </div>
                    )}
                  </div>
                  {/* Story count badge if multiple */}
                  {u.stories?.length > 1 && (
                    <div style={{ position: 'absolute', top: 0, right: 0, width: 16, height: 16, borderRadius: '50%',
                      background: '#FFD700', border: '1.5px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 8, fontWeight: 900, color: M }}>{u.stories.length}</span>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 9, color: '#374151', fontWeight: 600, textAlign: 'center', width: 56,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.name?.split(' ')[0]}
                </div>
                {/* Show total views + likes to story owner */}
                {u.user_id === profile?.id && (
                  <div style={{ fontSize: 8, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <EyeOutlined style={{ fontSize: 8 }} />
                      {u.stories.reduce((sum, st) => sum + (st.view_count || 0), 0)}
                    </span>
                    <span>❤️{u.stories.reduce((sum, st) => sum + (st.like_count || 0), 0)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Story Viewer Modal */}
      {storyViewer && (() => {
        const u = stories[storyViewer.userIdx];
        const s = u?.stories?.[storyViewer.storyIdx];
        if (!u || !s) { setStoryViewer(null); return null; }
        const canNext = storyViewer.storyIdx < u.stories.length - 1 || storyViewer.userIdx < stories.length - 1;
        const canPrev = storyViewer.storyIdx > 0 || storyViewer.userIdx > 0;
        const isOwnStory = u.user_id === profile?.id;
        // Record view for others' stories (fire-and-forget, no re-render)
        if (!isOwnStory) {
          axios.patch(`${baseUrl}/member/stories/${s.id}/view`, {}, { headers: authHeader() }).catch(() => {});
        }
        const handleLike = async () => {
          try {
            const r = await axios.post(`${baseUrl}/member/stories/${s.id}/like`, {}, { headers: authHeader() });
            setStories(prev => prev.map(usr => ({
              ...usr,
              stories: usr.stories.map(st => st.id !== s.id ? st : {
                ...st, liked_by_me: r.data.liked, like_count: r.data.like_count,
              }),
            })));
          } catch { /* silent */ }
        };
        return (
          <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setStoryViewer(null)}>
            <div style={{ position: 'relative', width: '100%', maxWidth: 400, height: '100%', maxHeight: 700 }}
              onClick={e => e.stopPropagation()}>
              {/* Progress bars */}
              <div style={{ position: 'absolute', top: 8, left: 8, right: 8, display: 'flex', gap: 3, zIndex: 10 }}>
                {u.stories.map((_, i) => (
                  <div key={i} style={{ flex: 1, height: 2, background: i <= storyViewer.storyIdx ? '#fff' : 'rgba(255,255,255,0.3)', borderRadius: 2 }} />
                ))}
              </div>
              {/* Close */}
              <button onClick={() => setStoryViewer(null)}
                style={{ position: 'absolute', top: 20, right: 12, background: 'none', border: 'none', color: '#fff', fontSize: 24, zIndex: 10, cursor: 'pointer' }}>×</button>
              {/* Delete — only own stories */}
              {u.user_id === profile?.id && (
                <button
                  onClick={async () => {
                    try {
                      await axios.delete(`${baseUrl}/member/stories/${s.id}`, { headers: authHeader() });
                      setStories(prev =>
                        prev.map(usr => usr.user_id !== u.user_id ? usr
                          : { ...usr, stories: usr.stories.filter(st => st.id !== s.id) }
                        ).filter(usr => usr.stories.length > 0)
                      );
                      setStoryViewer(null);
                    } catch { notification.error({ message: 'Delete failed', placement: 'topRight' }); }
                  }}
                  title="Story हटाएं"
                  style={{ position: 'absolute', top: 20, right: 48, background: 'rgba(220,38,38,0.75)', border: 'none', color: '#fff', zIndex: 10, cursor: 'pointer', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DeleteOutlined style={{ fontSize: 13 }} />
                </button>
              )}
              {/* User info */}
              <div style={{ position: 'absolute', top: 28, left: 12, display: 'flex', alignItems: 'center', gap: 8, zIndex: 10 }}>
                {u.photo ? <img src={u.photo} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '2px solid #fff' }} /> : null}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{u.name}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>{u.village || ''}</div>
                </div>
              </div>
              {/* Story image */}
              <img src={s.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              {/* Caption */}
              {s.caption && (
                <div style={{ position: 'absolute', bottom: 36, left: 0, right: 0, background: 'linear-gradient(transparent,rgba(0,0,0,0.8))',
                  padding: '40px 16px 14px', fontSize: 13, color: '#fff', fontWeight: 500 }}>
                  {s.caption}
                </div>
              )}
              {/* Bottom bar: views (owner only) + like button (all) */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'rgba(0,0,0,0.55)', padding: '7px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10 }}>
                {/* View count — owner only */}
                {isOwnStory ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#fff', fontWeight: 600 }}>
                      <EyeOutlined style={{ fontSize: 13 }} /> {s.view_count || 0}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#fda4af', fontWeight: 600 }}>
                      ❤️ {s.like_count || 0}
                    </span>
                  </div>
                ) : <div />}
                {/* Like button — not for own stories */}
                {!isOwnStory && (
                  <button onClick={handleLike} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 5,
                    color: s.liked_by_me ? '#f43f5e' : '#fff', fontSize: 13, fontWeight: 700,
                    padding: '4px 8px', borderRadius: 20,
                    background: s.liked_by_me ? 'rgba(244,63,94,0.2)' : 'rgba(255,255,255,0.1)',
                  }}>
                    {s.liked_by_me ? '❤️' : '🤍'} {s.like_count || 0}
                  </button>
                )}
              </div>
              {/* Nav areas */}
              {canPrev && <div style={{ position: 'absolute', left: 0, top: '20%', width: '30%', height: '60%', cursor: 'pointer' }}
                onClick={() => {
                  if (storyViewer.storyIdx > 0) setStoryViewer(p => ({...p, storyIdx: p.storyIdx-1}));
                  else if (storyViewer.userIdx > 0) setStoryViewer(p => ({userIdx: p.userIdx-1, storyIdx: stories[p.userIdx-1].stories.length-1}));
                }} />}
              {canNext && <div style={{ position: 'absolute', right: 0, top: '20%', width: '30%', height: '60%', cursor: 'pointer' }}
                onClick={() => {
                  if (storyViewer.storyIdx < u.stories.length-1) setStoryViewer(p => ({...p, storyIdx: p.storyIdx+1}));
                  else if (storyViewer.userIdx < stories.length-1) setStoryViewer(p => ({userIdx: p.userIdx+1, storyIdx: 0}));
                  else setStoryViewer(null);
                }} />}
            </div>
          </div>
        );
      })()}

      {/* ── 🙏 आज के समर्पण Viewer ── */}
      {samarpanViewer !== null && todayPays.length > 0 && (() => {
        const idx = Math.min(samarpanViewer, todayPays.length - 1);
        const p   = todayPays[idx];
        const ini = (n) => (n||'?').trim().split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
        const showAmt = Number(p.amount) >= 10000;

        const goNext = () => idx < todayPays.length - 1 ? setSamarpanViewer(idx + 1) : setSamarpanViewer(null);
        const goPrev = () => idx > 0 && setSamarpanViewer(idx - 1);

        const doAct = async (kind) => {
          try {
            if (kind === 'thanks') {
              await axios.post(`${baseUrl}/member/notify/thanks`, { to_user_id: p.id }, { headers: authHeader() });
              setSamarpanThanked(s => new Set([...s, p.id]));
            } else {
              await axios.post(`${baseUrl}/member/contact-request`, { to_user_id: p.id }, { headers: authHeader() });
              setSamarpanConnected(s => new Set([...s, p.id]));
            }
          } catch {}
        };

        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#2a0000', display: 'flex', flexDirection: 'column' }}
            onClick={goNext}>

            {/* Progress bars */}
            <div style={{ display: 'flex', gap: 3, padding: '48px 12px 8px' }}>
              {todayPays.map((_, i) => (
                <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < idx ? '#fff' : i === idx ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.25)' }} />
              ))}
            </div>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 14px 10px' }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>🙏 आज के समर्पण · {idx+1}/{todayPays.length}</span>
              <button onClick={(e) => { e.stopPropagation(); setSamarpanViewer(null); }}
                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32,
                  color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>

            {/* Card */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px' }}
              onClick={(e) => e.stopPropagation()}>
              <div style={{ width: '100%', maxWidth: 360, background: '#fffaf0', borderRadius: 20,
                border: `3px solid ${G}`, overflow: 'hidden', boxShadow: `0 0 40px rgba(255,215,0,0.3)` }}>
                {/* Arch header */}
                <div style={{ background: M, padding: '14px 20px 28px', textAlign: 'center',
                  borderBottomLeftRadius: '50%', borderBottomRightRadius: '50%', borderBottom: `3px solid ${G}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#fde68a', letterSpacing: 1 }}>॥ ॐ ब्रह्मणे नमः ॥</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: G, marginTop: 4 }}>समाज शिक्षा निधि</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#fff7ed' }}>समर्पण योजना</div>
                  <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 4, letterSpacing: 4 }}>❀ ✿ ❀ ✿ ❀</div>
                </div>
                {/* Photo */}
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: -22 }}>
                  <div style={{ width: 100, height: 100, borderRadius: '50%', background: G, padding: 3,
                    boxShadow: `0 0 20px rgba(255,215,0,0.6)` }}>
                    <div style={{ width: '100%', height: '100%', borderRadius: '50%', overflow: 'hidden', background: '#fef2f2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {p.photo
                        ? <img src={p.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                        : <span style={{ fontSize: 28, fontWeight: 900, color: M }}>{ini(p.name)}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: -10, textAlign: 'center' }}>
                  <span style={{ background: M, border: `2px solid ${G}`, borderRadius: 12, padding: '2px 12px',
                    fontSize: 11, fontWeight: 900, color: G }}>🙏 समर्पण</span>
                </div>
                {/* Name + location */}
                <div style={{ textAlign: 'center', padding: '10px 16px 0', fontSize: 20, fontWeight: 900, color: M }}>{p.name}</div>
                {p.location && <div style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#92400e', marginTop: 4 }}>📍 {p.location}</div>}
                {/* Message panel */}
                <div style={{ margin: '12px 16px', background: '#fff7ed', border: '1px solid #fed7aa',
                  borderRadius: 12, padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 13, color: '#57534e' }}>समाज के शैक्षिक विकास हेतु</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#7c2d12' }}>समाज शिक्षा निधि में समर्पण किया</div>
                  {showAmt && (
                    <div style={{ marginTop: 8, display: 'inline-block', background: '#15803d',
                      border: `3px solid ${G}`, borderRadius: 30, padding: '2px 20px',
                      fontSize: 22, fontWeight: 900, color: '#fff' }}>
                      ₹{Number(p.amount).toLocaleString('en-IN')}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: '#a8a29e', marginTop: 8, fontWeight: 600 }}>
                    {new Date().toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                </div>
                {/* Footer */}
                <div style={{ background: M, borderTop: `3px solid ${G}`, padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: G }}>✨ शुभाशीष · हार्दिक अभिनंदन ✨</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#fff', marginTop: 3 }}>अखिल भारतीय राजपुरोहित समाज विकास संस्थान</div>
                </div>
              </div>
            </div>

            {/* Tap prev zone */}
            <div style={{ position: 'absolute', top: 80, bottom: 120, left: 0, width: '35%', cursor: 'pointer' }}
              onClick={(e) => { e.stopPropagation(); goPrev(); }} />

            {/* Actions */}
            {p.id && (
              <div style={{ display: 'flex', gap: 10, padding: '10px 16px 32px' }} onClick={e => e.stopPropagation()}>
                <button
                  disabled={samarpanThanked.has(p.id)}
                  onClick={() => doAct('thanks')}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    background: samarpanThanked.has(p.id) ? '#fef3c7' : '#fff', border: 'none',
                    borderRadius: 12, padding: '12px 0', cursor: samarpanThanked.has(p.id) ? 'default' : 'pointer',
                    fontSize: 14, fontWeight: 800, color: samarpanThanked.has(p.id) ? '#92400e' : '#ef4444' }}>
                  {samarpanThanked.has(p.id) ? '❤️ धन्यवाद दिया ✓' : '🤍 धन्यवाद'}
                </button>
                <button
                  disabled={samarpanConnected.has(p.id)}
                  onClick={() => doAct('connect')}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    background: samarpanConnected.has(p.id) ? '#dcfce7' : '#fff', border: 'none',
                    borderRadius: 12, padding: '12px 0', cursor: samarpanConnected.has(p.id) ? 'default' : 'pointer',
                    fontSize: 14, fontWeight: 800, color: samarpanConnected.has(p.id) ? '#15803d' : M }}>
                  {samarpanConnected.has(p.id) ? '🔗 जुड़े ✓' : '🔗 संपर्क'}
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Daily Quote + Panchang ── */}
      {(() => {
        const panch = getTodayPanchang();
        return (
          <div style={{ margin: '0 0 14px', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            {/* Panchang strip — always shown */}
            <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '7px 14px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px' }}>
              <span style={{ fontSize: 13 }}>🪔</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: M, fontFamily: 'serif' }}>
                {panch.vaara}
              </span>
              <span style={{ width: 1, height: 12, background: '#d97706', opacity: 0.4, display: 'inline-block' }} />
              <span style={{ fontSize: 10, color: '#92400e' }}>
                {panch.masa} मास · {panch.paksha}
              </span>
              <span style={{ width: 1, height: 12, background: '#d97706', opacity: 0.4, display: 'inline-block' }} />
              <span style={{ fontSize: 10, color: '#92400e' }}>
                {panch.tithi}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 9, color: '#b45309', fontFamily: 'serif', fontWeight: 700 }}>
                वि.सं. {panch.samvat}
              </span>
            </div>
            {/* Quote */}
            {dailyQuote && (
              dailyQuote.image_url ? (
                <div>
                  {/* Full image — natural height, not cropped */}
                  <img src={dailyQuote.image_url} alt="आज का विचार"
                    style={{ width: '100%', display: 'block', maxHeight: 480, objectFit: 'contain', background: '#000' }}
                    onClick={() => window.open(dailyQuote.image_url, '_blank')}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  {/* Quote text below image if present */}
                  {(dailyQuote.quote_text || dailyQuote.quote_hn) && (
                    <div style={{ background: `linear-gradient(135deg, ${M} 0%, #b71c1c 100%)`, padding: '12px 20px' }}>
                      <div style={{ fontSize: 14, color: '#fff', fontFamily: 'serif', lineHeight: 1.7, fontStyle: 'italic' }}>
                        "{dailyQuote.quote_text || dailyQuote.quote_hn}"
                      </div>
                      {dailyQuote.author && (
                        <div style={{ fontSize: 12, color: G, marginTop: 8, fontWeight: 600, textAlign: 'right' }}>
                          — {dailyQuote.author}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ background: `linear-gradient(135deg, ${M} 0%, #b71c1c 100%)`, padding: '16px 20px' }}>
                  <div style={{ fontSize: 11, color: G, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8 }}>
                    ✨ {t('आज का विचार', "Today's Thought")}
                  </div>
                  <div style={{ fontSize: 15, color: '#fff', fontFamily: 'serif', lineHeight: 1.7, fontStyle: 'italic' }}>
                    "{dailyQuote.quote_text || dailyQuote.quote_hn}"
                  </div>
                  {dailyQuote.author && (
                    <div style={{ fontSize: 12, color: G, marginTop: 10, fontWeight: 600, textAlign: 'right' }}>
                      — {dailyQuote.author}
                    </div>
                  )}
                </div>
              )
            )}
            {/* Show panchang-only card when no quote */}
            {!dailyQuote && (
              <div style={{ background: '#fff8f0', padding: '12px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: M, fontWeight: 700, fontFamily: 'serif' }}>🙏 जय खेतेश्वर 🙏</div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── FEATURE GRID — 4×2 tiles ── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '12px 14px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          <SearchOutlined style={{ fontSize: 12 }} /> {t('सुविधाएं', 'Features')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7 }}>
          {[
            { icon: <BankOutlined />,        label: t('शिक्षा समर्पण','Payments'),       tab: 'payments',     color: M        },
            { icon: <CameraOutlined />,      label: t('गैलरी',        'Gallery'),         tab: 'gallery',      color: '#7c3aed' },
            { icon: <AppstoreOutlined />,    label: t('सेवाएं',       'Services'),        tab: 'services',     color: '#db2777' },
            { icon: <TrophyOutlined />,      label: t('योगदान',       'Contribution'),    tab: 'community',    color: '#d97706' },
            { icon: <ReadOutlined />,        label: t('शिक्षा सहायता','Student Support'), tab: 'student-help', color: '#b45309' },
            { icon: <BuildOutlined />,       label: t('विकास',        'Development'),     tab: 'development',  color: '#7c3aed' },
            { icon: <EnvironmentOutlined />, label: t('गाँव',         'Village'),         tab: 'villages',     color: '#16a34a' },
            { icon: <RiseOutlined />,        label: t('रोजगार',       'Jobs'),            tab: 'jobs',         color: '#6d28d9' },
            { icon: <SoundOutlined />,       label: t('समाचार',       'News'),            tab: 'samachar',     color: '#0891b2' },
            { icon: <UsergroupAddOutlined />,label: t('रेफर करें',    'Refer'),           tab: 'referral',     color: '#dc2626' },
          ].map(item => (
            <button key={item.tab + (item.subTab||'')} onClick={() => onSwitchTab(item.tab, item.subTab)} style={{
              background: `${item.color}12`,
              border: `1.5px solid ${item.color}30`,
              borderRadius: 11, padding: '10px 4px',
              cursor: 'pointer', textAlign: 'center',
              boxShadow: `0 1px 4px ${item.color}15`,
            }}>
              <div style={{ fontSize: 20, marginBottom: 5, lineHeight: 1, color: item.color }}>{item.icon}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: item.color, lineHeight: 1.3 }}>{item.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Samaj Connect: pending requests + suggestions ── */}
      <SamajConnectWidget tabBadges={tabBadges} profile={profile} onSwitchTab={onSwitchTab} onOpenProfile={setOnlineProfileId} />

      {/* Streak + Member card merged into hero banner above — no separate cards needed */}

      {/* ── Check-in Welcome Popup (bottom center, first visit of day) ── */}
      {checkinPopup && !checkin?.checked_today && (
        <div style={{
          position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1500, width: 300, maxWidth: 'calc(100vw - 32px)',
          background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          border: '1px solid #e5e7eb', overflow: 'hidden',
          animation: 'reqSlideUp 0.3s ease',
        }}>
          <div style={{ background: `linear-gradient(135deg,${M},#b71c1c)`, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>🙏</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#fff', flex: 1 }}>आज का नमस्कार</span>
            <button onClick={() => { setCheckinPopup(false); sessionStorage.setItem('checkinPopupSeen', '1'); }}
              style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 20, height: 20, borderRadius: '50%', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
          <div style={{ padding: '14px 16px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>
              जय खेतेश्वर! {checkin?.streak > 0 ? `आपकी 🔥 ${checkin.streak} दिन की streak है।` : 'आज पहला check-in करें।'}
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 14 }}>रोज़ नमस्कार करें — streak बनाएं 🏆</div>
            <button
              disabled={checkinLoading}
              onClick={async () => {
                setCheckinLoading(true);
                try {
                  const r = await axios.post(`${baseUrl}/member/checkin`, {}, { headers: authHeader() });
                  if (r.data.status) {
                    setCheckin(prev => ({ ...prev, checked_today: true, streak: r.data.streak, total: r.data.total }));
                    setCheckinDone(true);
                    sessionStorage.setItem('checkinPopupSeen', '1');
                    setTimeout(() => { setCheckinPopup(false); setCheckinDone(false); }, 1800);
                  }
                } catch {} finally { setCheckinLoading(false); }
              }}
              style={{
                width: '100%', background: checkinDone ? '#15803d' : M, color: '#fff',
                border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 13, fontWeight: 900, cursor: 'pointer',
                transition: 'background 0.3s',
              }}>
              {checkinDone ? '✅ जय खेतेश्वर! 🙏' : checkinLoading ? '…' : '🙏 नमस्कार करें'}
            </button>
          </div>
        </div>
      )}

      {/* ── Caption Modal for Story upload ── */}
      {captionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={e => e.target === e.currentTarget && setCaptionModal(false)}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 340, padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#1c1e21', marginBottom: 4 }}>Story Caption</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 14 }}>Optional — कुछ लिखना हो तो लिखें</div>
            <textarea
              autoFocus
              value={captionText}
              onChange={e => setCaptionText(e.target.value)}
              placeholder="कुछ लिखें… (optional)"
              maxLength={300}
              rows={3}
              style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box', color: '#1c1e21' }}
            />
            <div style={{ fontSize: 10, color: '#d1d5db', textAlign: 'right', marginBottom: 14 }}>{captionText.length}/300</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setCaptionModal(false); setPendingStoryUrl(''); setCaptionText(''); }}
                style={{ flex: 1, background: '#f3f4f6', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#6b7280' }}>
                रद्द करें
              </button>
              <button disabled={captionSaving}
                onClick={async () => {
                  setCaptionSaving(true);
                  try {
                    await axios.post(`${baseUrl}/member/stories`, { photo_url: pendingStoryUrl, caption: captionText.trim() || null }, { headers: authHeader() });
                    const r = await axios.get(`${baseUrl}/member/stories`, { headers: authHeader() });
                    if (r.data.status) setStories(r.data.result || []);
                    notification.success({ message: '✅ Story posted!', placement: 'topRight', duration: 2 });
                  } catch { notification.error({ message: 'Story save failed', placement: 'topRight' }); }
                  finally { setCaptionSaving(false); setCaptionModal(false); setPendingStoryUrl(''); setCaptionText(''); }
                }}
                style={{ flex: 1, background: M, border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 13, fontWeight: 800, cursor: 'pointer', color: '#fff' }}>
                {captionSaving ? '…' : 'Post करें 🌸'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mission 21K quick-join banner (shown right after greeting if not enrolled) ── */}
      {dash.mission_21k_visible !== false && !dash.my_mission_status?.enrolled && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12, padding: '10px 14px', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#7a0000' }}>Mission 21K में जुड़ें</div>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>₹200/माह • सामूहिक सदस्यता अभियान</div>
          </div>
          <button onClick={handleJoinMission21K} disabled={missionJoining} style={{
            background: missionJoining ? '#e5e7eb' : '#7a0000', color: '#fff',
            border: 'none', borderRadius: 10, padding: '9px 18px',
            fontSize: 13, fontWeight: 900, cursor: missionJoining ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
          }}>
            {missionJoining ? 'जुड़ रहे हैं…' : '+ Join करें'}
          </button>
        </div>
      )}

      {/* ── Auto-pay offer after Mission 21K join success ── */}
      {missionAutoPayOffer && (
        <div style={{
          background: 'linear-gradient(135deg,#fffbeb,#fef3c7)',
          border: '2px solid #fbbf24', borderRadius: 14,
          padding: '14px 16px', marginBottom: 14,
        }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#92400e', marginBottom: 4 }}>
            🎉 Mission 21K में जुड़ गए! अब Auto-pay लगाएं
          </div>
          <div style={{ fontSize: 11, color: '#b45309', marginBottom: 12, lineHeight: 1.5 }}>
            हर माह ₹200 याद रखने की जरूरत नहीं — एक बार UPI Autopay setup करें,
            बाकी automatically होगा।
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              disabled={missionAutoPayLoading}
              onClick={handleEnableAutoPayFromMission}
              style={{
                flex: 1, background: '#92400e', color: '#fef3c7',
                border: 'none', borderRadius: 9, padding: '10px 0',
                fontSize: 12, fontWeight: 800, cursor: 'pointer',
                opacity: missionAutoPayLoading ? 0.6 : 1,
              }}>
              {missionAutoPayLoading ? 'Setup हो रहा है…' : '⚡ Auto-pay Enable करें'}
            </button>
            <button
              onClick={() => setMissionAutoPayOffer(false)}
              style={{
                background: 'none', color: '#b45309',
                border: '1px solid #fbbf24', borderRadius: 9, padding: '10px 14px',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
              बाद में
            </button>
          </div>
        </div>
      )}

      {/* ── Samarpan Day reminder ── */}
      {(() => {
        if (!dash.joining_date || dash.this_month_paid || !dash.announced_amount || dash.payment_commitment === 'one_time') return null;
        const joiningDay = new Date(dash.joining_date).getDate();
        const todayDay   = new Date().getDate();
        if (joiningDay !== todayDay) return null;
        return (
          <div style={{
            background: `linear-gradient(135deg, ${M} 0%, #b71c1c 60%, #c97900 100%)`,
            borderRadius: 14, padding: '14px 16px', marginBottom: 14,
            border: `1.5px solid ${G}`,
            boxShadow: `0 4px 20px rgba(122,0,0,0.3)`,
            display: 'flex', alignItems: 'center', gap: 14,
            animation: 'samarpan-glow 2s ease-in-out infinite',
          }}>
            <style>{`@keyframes samarpan-glow { 0%,100%{box-shadow:0 4px 20px rgba(122,0,0,0.3),0 0 0 0 rgba(255,215,0,0.4)} 50%{box-shadow:0 4px 28px rgba(122,0,0,0.5),0 0 0 8px rgba(255,215,0,0)} }`}</style>
            <div style={{ fontSize: 36, flexShrink: 0, lineHeight: 1 }}>🙏</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: G, marginBottom: 3 }}>
                आज आपका समर्पण दिवस है!
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>
                आपने ₹{Number(dash.announced_amount).toLocaleString('hi-IN')}/माह का संकल्प लिया है।<br/>
                इस माह का योगदान अभी तक दर्ज नहीं है।
              </div>
            </div>
            <button
              onClick={() => onSwitchTab('payments')}
              style={{
                flexShrink: 0, background: G, color: M, border: 'none',
                borderRadius: 10, padding: '9px 14px',
                fontSize: 12, fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
              योगदान देवें →
            </button>
          </div>
        );
      })()}

      {/* Admin 24hr Status Photos are now shown inline after post 1 — see posts.map below */}

      {/* Admin Photo Full Viewer */}
      {adminPhotoView !== null && adminPhotos[adminPhotoView.idx] && (() => {
        const p = adminPhotos[adminPhotoView.idx];
        const canPrev = adminPhotoView.idx > 0;
        const canNext = adminPhotoView.idx < adminPhotos.length - 1;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setAdminPhotoView(null)}>
            <div style={{ position: 'relative', width: '100%', maxWidth: 440, maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              onClick={e => e.stopPropagation()}>
              <button onClick={() => setAdminPhotoView(null)}
                style={{ position: 'absolute', top: -36, right: 8, background: 'none', border: 'none', color: '#fff', fontSize: 26, cursor: 'pointer', zIndex: 10 }}>×</button>
              <img src={p.photo_url} alt={p.title || ''} style={{ width: '100%', maxHeight: '75vh', objectFit: 'contain', borderRadius: 10 }} />
              {(p.title || p.caption) && (
                <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 14px', marginTop: 10, width: '100%', backdropFilter: 'blur(10px)' }}>
                  {p.title   && <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 4 }}>{p.title}</div>}
                  {p.caption && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>{p.caption}</div>}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                {canPrev && <button onClick={() => setAdminPhotoView({ idx: adminPhotoView.idx - 1 })}
                  style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 20, padding: '6px 16px', cursor: 'pointer', fontSize: 13 }}>← Prev</button>}
                {canNext && <button onClick={() => { setAdminPhotoView({ idx: adminPhotoView.idx + 1 }); axios.patch(`${baseUrl}/member/status-photos/${adminPhotos[adminPhotoView.idx+1].id}/view`, {}, { headers: authHeader() }).catch(()=>{}); }}
                  style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 20, padding: '6px 16px', cursor: 'pointer', fontSize: 13 }}>Next →</button>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── My Post Activity Card ── */}
      {myPosts !== null && (() => {
        const totalReactions = myPosts.reduce((s, p) => s + (p.reaction_count || 0), 0);
        const totalComments  = myPosts.reduce((s, p) => s + (p.comment_count  || 0), 0);
        const hasActivity    = totalReactions > 0 || totalComments > 0;
        const hasNoPosts     = myPosts.length === 0;

        if (hasNoPosts) {
          return (
            <div onClick={() => setWriteOpen(true)} style={{
              background: 'linear-gradient(135deg, #ede9fe, #f5f3ff)',
              border: '1.5px dashed #c4b5fd', borderRadius: 14,
              padding: '12px 16px', marginBottom: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <EditOutlined style={{ fontSize: 18, color: '#fff' }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#6d28d9', marginBottom: 2 }}>
                  Community में पहली post करें!
                </div>
                <div style={{ fontSize: 11, color: '#7c3aed' }}>
                  अपने विचार, खबर या उपलब्धि हजारों सदस्यों के साथ share करें →
                </div>
              </div>
            </div>
          );
        }

        if (hasActivity) {
          return (
            <div onClick={() => onSwitchTab?.('board', 'mine')} style={{
              background: 'linear-gradient(135deg, #fef3c7, #fffbeb)',
              border: '1.5px solid #fbbf24', borderRadius: 14,
              padding: '12px 16px', marginBottom: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <BellFilled style={{ fontSize: 18, color: '#fff' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#92400e', marginBottom: 4 }}>
                  आपके post पर activity आई!
                </div>
                <div style={{ fontSize: 11, color: '#b45309', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {totalReactions > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><LikeOutlined style={{ fontSize: 12 }} /> {totalReactions} reactions</span>}
                  {totalComments  > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MessageOutlined style={{ fontSize: 12 }} /> {totalComments} टिप्पणियाँ</span>}
                  <span style={{ color: '#d97706', fontWeight: 700 }}>देखें →</span>
                </div>
              </div>
              <div style={{ fontSize: 9, fontWeight: 700, background: '#f59e0b', color: '#fff', borderRadius: 8, padding: '3px 8px' }}>NEW</div>
            </div>
          );
        }

        return (
          <div onClick={() => setWriteOpen(true)} style={{
            background: '#f0fdf4', border: '1.5px dashed #86efac',
            borderRadius: 14, padding: '12px 16px', marginBottom: 14, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <NotificationOutlined style={{ fontSize: 18, color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#15803d', marginBottom: 2 }}>
                Community आपका इंतज़ार कर रही है
              </div>
              <div style={{ fontSize: 11, color: '#16a34a' }}>
                आज कुछ नया share करें — विचार, खबर, उपलब्धि →
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── समाज अपडेट्स — above composer ── */}
      {adminPhotos.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
            समाज अपडेट्स
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4 }}>
            {adminPhotos.map((ph, phIdx) => (
              <div key={ph.id}
                onClick={async () => {
                  setAdminPhotoView({ idx: phIdx });
                  axios.patch(`${baseUrl}/member/status-photos/${ph.id}/view`, {}, { headers: authHeader() }).catch(() => {});
                }}
                style={{ flexShrink: 0, cursor: 'pointer', position: 'relative', borderRadius: 10, overflow: 'hidden', width: 100, height: 160, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                <img src={ph.photo_url} alt={ph.title || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(transparent 50%, rgba(0,0,0,0.7))', borderRadius: 10 }} />
                {ph.title && (
                  <div style={{ position: 'absolute', bottom: 6, left: 6, right: 6, fontSize: 9, fontWeight: 700, color: '#fff', lineHeight: 1.3,
                    overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {ph.title}
                  </div>
                )}
                <div style={{ position: 'absolute', inset: 0, borderRadius: 10, border: '2px solid #ef4444', pointerEvents: 'none' }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── आज के शीर्ष समर्पणकर्ता — above composer ── */}
      {dash?.today_top_donor && (() => {
        const d = dash.today_top_donor;
        const ini = n => (n||'?').trim().split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase();
        return (
          <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${M}30`, borderLeft: `4px solid ${M}`, marginBottom: 14, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px 0' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>🏆 आज के शीर्ष समर्पणकर्ता</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 12 }}>
                {d.photo
                  ? <img src={d.photo} alt="" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${M}`, flexShrink: 0 }} />
                  : <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: M, flexShrink: 0 }}>{ini(d.name)}</div>
                }
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{d.name}</div>
                  {d.village && <div style={{ fontSize: 11, color: '#6b7280' }}>📍 {d.village}</div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 900, color: M }}>₹{Number(d.amount).toLocaleString('en-IN')}</div>
                  <div style={{ fontSize: 9, color: '#9ca3af' }}>आज का समर्पण</div>
                </div>
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${M}15`, background: `${M}06`, padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, color: '#6b7280' }}>आज के सभी समर्पणकर्ता देखें</span>
              <button onClick={() => onSwitchTab('community')}
                style={{ background: M, color: '#fff', border: 'none', borderRadius: 20,
                  padding: '5px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4 }}>
                <RiseOutlined /> योगदान देखें
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Post Composer ── */}
      <style>{`
        @keyframes composer-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(122,0,0,0.35), 0 4px 18px rgba(122,0,0,0.15); }
          50%       { box-shadow: 0 0 0 7px rgba(122,0,0,0),  0 4px 24px rgba(122,0,0,0.28); }
        }
        @keyframes composer-badge-blink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.6; transform: scale(0.92); }
        }
        .composer-card { animation: composer-pulse 2.2s ease-in-out infinite; }
        .composer-badge { animation: composer-badge-blink 1.4s ease-in-out infinite; }
      `}</style>
      <div className="composer-card" style={{
        background: '#fff', borderRadius: 16,
        border: `2px solid ${M}`,
        marginBottom: 14, overflow: 'hidden',
      }}>
        {/* Header strip */}
        <div style={{
          background: `linear-gradient(90deg, ${M} 0%, #b71c1c 60%, #c97900 100%)`,
          padding: '7px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: G, display: 'flex', alignItems: 'center', gap: 6, letterSpacing: 0.3 }}>
            <EditOutlined style={{ fontSize: 13 }} /> {t('समाज मंच में साझा करें', 'Share on Community Board')}
          </span>
          <span className="composer-badge" style={{
            background: G, color: M, fontSize: 9, fontWeight: 900,
            borderRadius: 8, padding: '2px 8px', letterSpacing: 0.4,
          }}>
            {t('नई पोस्ट', 'NEW POST')} +
          </span>
        </div>

        {/* Avatar + prompt row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 0' }}>
          {profile?.profile_img
            ? <img src={profile.profile_img} alt="" style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${M}`, flexShrink: 0 }} />
            : <div style={{ width: 42, height: 42, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: G, flexShrink: 0 }}>
                {(profile?.name_hn || profile?.name_eng || '?').trim().split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()}
              </div>
          }
          <button onClick={() => setWriteOpen(true)} style={{
            flex: 1, textAlign: 'left',
            background: `${M}08`, border: `1.5px dashed ${M}50`,
            borderRadius: 24, padding: '11px 16px', fontSize: 13,
            color: `${M}90`, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
          }}>
            {(profile?.name_hn || profile?.name_eng)
              ? `${(profile.name_hn || profile.name_eng).split(' ')[0]} जी, कुछ साझा करें… 🙏`
              : 'अपने विचार साझा करें… 🙏'}
          </button>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, padding: '10px 14px 12px' }}>
          <button onClick={() => setWriteOpen(true)} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            background: '#ede9fe', border: '1.5px solid #c4b5fd',
            borderRadius: 10, padding: '8px 4px', cursor: 'pointer',
            color: '#6d28d9', fontWeight: 800, fontSize: 12,
          }}>
            <EditOutlined style={{ fontSize: 14 }} /> {t('पोस्ट', 'Post')}
          </button>
          <button onClick={() => setWriteOpen(true)} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            background: '#dcfce7', border: '1.5px solid #86efac',
            borderRadius: 10, padding: '8px 4px', cursor: 'pointer',
            color: '#15803d', fontWeight: 800, fontSize: 12,
          }}>
            <CameraOutlined style={{ fontSize: 14 }} /> {t('फ़ोटो', 'Photo')}
          </button>
          <button onClick={() => setWriteOpen(true)} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            background: '#fef9c3', border: '1.5px solid #fde047',
            borderRadius: 10, padding: '8px 4px', cursor: 'pointer',
            color: '#a16207', fontWeight: 800, fontSize: 12,
          }}>
            <BulbOutlined style={{ fontSize: 14 }} /> {t('विचार', 'Thought')}
          </button>
        </div>
      </div>

      {/* ── Featured Donor Business — always visible above feed ── */}
      {dash?.featured_donor_business && (() => {
        const biz = dash.featured_donor_business;
        const bannerPhotos = biz.business_photo ? biz.business_photo.split(',').filter(Boolean) : [];
        const banner = bannerPhotos[0];
        const location = [biz.biz_village, biz.current_city, biz.district].filter(Boolean).join(', ');
        return (
          <div style={{ background: '#fff', borderRadius: 16, border: `1.5px solid ${M}25`, marginBottom: 14, overflow: 'hidden' }}>
            <div style={{ background: `linear-gradient(90deg, ${M}, #b71c1c)`, padding: '6px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: G, letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 5 }}>
                <TrophyOutlined style={{ fontSize: 12 }} /> आज के समर्पणकर्ता का व्यवसाय
              </span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
                ₹{Number(biz.donor_amount).toLocaleString('en-IN')} समर्पण ✓
              </span>
            </div>
            {banner
              ? <img src={banner} alt={biz.firm_name || ''} style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }} />
              : <div style={{ height: 60, background: `linear-gradient(135deg, ${M}12, ${M}05)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShopOutlined style={{ fontSize: 28, color: `${M}40` }} />
                </div>
            }
            <div style={{ padding: '12px 14px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                {(biz.biz_owner_photo || biz.donor_photo)
                  ? <img src={biz.biz_owner_photo || biz.donor_photo} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${M}30`, flexShrink: 0 }} />
                  : <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${M}15`, border: `2px solid ${M}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: M, flexShrink: 0 }}>
                      {(biz.donor_name || '?').trim().split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase()}
                    </div>
                }
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#111827' }}>{biz.donor_name}</div>
                  {biz.firm_name && <div style={{ fontSize: 11, color: M, fontWeight: 700 }}>{biz.firm_name}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                {biz.sector_name && <span style={{ fontSize: 10, fontWeight: 700, color: M, background: `${M}12`, borderRadius: 20, padding: '2px 10px' }}>{biz.sector_name}</span>}
                {biz.sub_sector_label && <span style={{ fontSize: 10, color: '#5b21b6', background: '#ede9fe', borderRadius: 20, padding: '2px 10px' }}>{biz.sub_sector_label}</span>}
              </div>
              {biz.establishment_year && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>स्थापना: {biz.establishment_year}</div>}
              {location && <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}><EnvironmentOutlined style={{ fontSize: 10 }} /> {location}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setFeaturedBizOpen(true); setFeaturedBizStatus(null); }} style={{
                  flex: 1, padding: '8px 0', borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: '#fff', fontSize: 12, fontWeight: 700, color: '#374151', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                }}>
                  <EyeOutlined /> विवरण
                </button>
                <button onClick={() => { setFeaturedBizOpen(true); setFeaturedBizStatus(null); }} style={{
                  flex: 1, padding: '8px 0', borderRadius: 10, border: 'none',
                  background: M, fontSize: 12, fontWeight: 700, color: G, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                }}>
                  <PhoneOutlined /> संपर्क अनुरोध
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── शिक्षा निधि योजना — Impact Card (before feed) ── */}
      {scholarshipImpact && scholarshipImpact.total > 0 && (() => {
        const { total, years = [], latest = [] } = scholarshipImpact;
        const latestYear = years[years.length - 1];
        const prevYears  = years.slice(0, -1);
        const fmtDate    = d => d ? new Date(d).toLocaleDateString('hi-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
        // Detect if a new beneficiary was added in the last 30 days
        const isNew = latest.length > 0 && latest[0]?.created_at &&
          (Date.now() - new Date(latest[0].created_at).getTime()) < 30 * 24 * 60 * 60 * 1000;
        return (
          <div style={{ background: '#fff', borderRadius: 18, marginBottom: 16, overflow: 'hidden', boxShadow: isNew ? '0 4px 20px rgba(122,0,0,0.15)' : '0 2px 12px rgba(0,0,0,0.07)', border: isNew ? '1.5px solid #fca5a5' : '1px solid #e5e7eb' }}>

            {/* Top strip */}
            <div style={{ height: 4, background: 'linear-gradient(90deg,#7a0000,#b45309,#fbbf24)' }} />

            {/* Header */}
            <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase' }}>ABRSVS · शिक्षा निधि योजना</span>
                    {isNew && (
                      <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: '#ef4444', borderRadius: 20, padding: '1px 7px', animation: 'pulse 1.5s infinite' }}>नया जुड़ा!</span>
                    )}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: '#111827' }}>आर्थिक सहयोग प्राप्त</div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: '#7a0000' }}>बालक-बालिका</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 42, fontWeight: 900, color: '#7a0000', lineHeight: 1 }}>{total}</div>
                  <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 1 }}>कुल विद्यार्थी</div>
                </div>
              </div>
            </div>

            <div style={{ padding: '10px 14px 12px' }}>

              {/* Current year */}
              {latestYear && (
                <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 12, padding: '10px 12px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 9, color: '#92400e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>इस वर्ष</div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: '#7a0000' }}>{latestYear.year}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 36, fontWeight: 900, color: '#b45309', lineHeight: 1 }}>{latestYear.total}</div>
                      <div style={{ fontSize: 9, color: '#92400e' }}>विद्यार्थी</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <div style={{ flex: 1, background: '#eff6ff', borderRadius: 8, padding: '6px 8px', textAlign: 'center', border: '1px solid #bfdbfe' }}>
                      <div style={{ fontSize: 18, fontWeight: 900, color: '#1d4ed8' }}>{latestYear.boys}</div>
                      <div style={{ fontSize: 9, color: '#3b82f6', fontWeight: 700, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} /> बालक
                      </div>
                    </div>
                    <div style={{ flex: 1, background: '#fdf2f8', borderRadius: 8, padding: '6px 8px', textAlign: 'center', border: '1px solid #fbcfe8' }}>
                      <div style={{ fontSize: 18, fontWeight: 900, color: '#be185d' }}>{latestYear.girls}</div>
                      <div style={{ fontSize: 9, color: '#ec4899', fontWeight: 700, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ec4899', display: 'inline-block' }} /> बालिका
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Previous years */}
              {prevYears.length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 10, overflowX: 'auto', scrollbarWidth: 'none' }}>
                  {prevYears.map(y => (
                    <div key={y.year} style={{ flex: '0 0 auto', minWidth: 66, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 8px', textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 900, color: '#374151' }}>{y.total}</div>
                      <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600, marginTop: 1 }}>{y.year}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Latest 5 students */}
              {latest.length > 0 && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0, display: 'inline-block' }} />
                    <span style={{ fontSize: 10, fontWeight: 800, color: '#374151' }}>
                      {latestYear ? `${latestYear.year} — इस वर्ष के विद्यार्थी` : 'नवीनतम विद्यार्थी'}
                    </span>
                  </div>
                  {latest.map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: i < latest.length - 1 ? '1px solid #f3f4f6' : 'none', background: '#fff' }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: s.gender === 'Male' ? '#dbeafe' : '#fce7f3', border: `2px solid ${s.gender === 'Male' ? '#93c5fd' : '#f9a8d4'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 900, color: s.gender === 'Male' ? '#1d4ed8' : '#be185d' }}>{s.gender === 'Male' ? '♂' : '♀'}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                        <div style={{ fontSize: 10, color: '#6b7280' }}>{s.village || ''}{s.district ? ` · ${s.district}` : ''}</div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 800, color: '#7a0000', background: '#fef2f2', borderRadius: 6, padding: '2px 6px' }}>{s.fiscal_year}</div>
                        <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 2 }}>{fmtDate(s.created_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* CTA */}
              <button onClick={() => onSwitchTab('development', 'shiksha')}
                style={{ width: '100%', background: '#7a0000', border: 'none', borderRadius: 10, padding: '10px 0', fontSize: 12, fontWeight: 800, color: '#fde68a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                🎓 पूरी सूची देखें — सभी वर्ष →
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── समाज मंच Feed — full PostCard + inline comments ── */}
      <div style={{ fontSize: 13, fontWeight: 800, color: '#7c3aed', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <TeamOutlined /> समाज मंच
      </div>

      {postsLoading && posts.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
      )}

      {posts.map((p, idx) => (
        <div key={p.id}>

          {/* ── Interstitial: Village contributors — moved to AFTER first post (see below PostCard) ── */}
          {false && idx === 0 && dash.village_contributors?.length > 0 && (() => {
            const list = dash.village_contributors;
            const village = list[0]?.village;
            const ini2 = n => (n || '?').trim().split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase();
            const avColors = ['#7c3aed','#0891b2','#15803d','#b45309','#be123c'];
            return (
              <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${M}25`, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: M, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  🏘️ {village ? `${village} से आज के समर्पण` : 'आपके गाँव से आज'}
                </div>
                {list.map((m, i) => {
                  const avColor = avColors[i % 5];
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: i < list.length - 1 ? 8 : 0, marginBottom: i < list.length - 1 ? 8 : 0, borderBottom: i < list.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      {m.photo
                        ? <img src={m.photo} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', flexShrink: 0, border: `1.5px solid ${avColor}40` }} />
                        : <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${avColor}15`, border: `2px solid ${avColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: avColor, flexShrink: 0 }}>{ini2(m.name)}</div>
                      }
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{m.name}</div>
                        {m.village && <div style={{ fontSize: 10, color: '#9ca3af' }}>📍 {m.village}</div>}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 20, padding: '3px 10px', flexShrink: 0 }}>
                        ✓ समर्पण किया{m.amount ? ` · ₹${Number(m.amount).toLocaleString('en-IN')}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}


          {/* ── Interstitial: Individual news cards at idx 1, 4, 8, 12 ── */}
          {[1, 4, 8, 12].includes(idx) && (() => {
            const newsIdx = [1, 4, 8, 12].indexOf(idx);
            const n = dash.recent_news?.[newsIdx];
            if (!n) return null;
            return (
              <div onClick={() => setOpenedNews(n)} style={{
                background: '#fff', borderRadius: 14,
                border: `1.5px solid #e5e7eb`,
                marginBottom: 14, cursor: 'pointer', overflow: 'hidden',
              }}>
                {n.image_url && (
                  <img src={n.image_url} alt="" style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} />
                )}
                <div style={{ padding: '10px 14px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                    {n.category_name && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: n.category_color || M, background: `${n.category_color || M}18`, borderRadius: 20, padding: '2px 8px' }}>
                        {n.category_name}
                      </span>
                    )}
                    {n.post_date && (
                      <span style={{ fontSize: 10, color: '#9ca3af' }}>
                        {new Date(n.post_date).toLocaleDateString('hi-IN', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                    <span style={{ fontSize: 9, color: '#d1d5db', marginLeft: 'auto' }}>📢 समाचार</span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', lineHeight: 1.4, marginBottom: 4 }}>{n.title}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
                    {(n.content || '').slice(0, 100)}{(n.content || '').length > 100 ? '…' : ''}
                  </div>
                  <div style={{ fontSize: 11, color: M, fontWeight: 700, marginTop: 6 }}>और पढ़ें →</div>
                </div>
              </div>
            );
          })()}

          {/* ── Interstitial card after post 5: Mission 21K ── */}
          {idx === 5 && dash && !dash.my_mission_status?.enrolled && (() => (
            <div style={{ background: 'linear-gradient(135deg,#fff5f5,#fef2f2)', borderRadius: 14, border: `1.5px solid ${M}40`, padding: '14px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: M, marginBottom: 4 }}>🎯 Mission 21K में जुड़ें</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>₹200/माह · सामूहिक सदस्यता अभियान</div>
              <button onClick={() => onSwitchTab('home')}
                style={{ background: M, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                + Join करें
              </button>
            </div>
          ))()}

          {/* ── Post card ── */}
          <div style={{ marginBottom: 14, borderRadius: 16, overflow: 'hidden', border: '1px solid #e4e6eb', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', background: '#fff' }}>
            <PostCard
              post={p}
              onOpen={post => setFeedOpenPost(post)}
              onReact={handleReact}
              isCommentExpanded={expandedPost === p.id}
              onToggleComment={() => setExpandedPost(expandedPost === p.id ? null : p.id)}
              myUserId={profile?.id}
              onDelete={id => setPosts(prev => prev.filter(x => x.id !== id))}
            />
            {expandedPost === p.id && (
              <InlineCommentSection post={p} myUserId={profile?.id} myProfile={profile} />
            )}
          </div>

          {/* ── After first post: Village contributors ── */}
          {idx === 0 && dash.village_contributors?.length > 0 && (() => {
            const list = dash.village_contributors;
            const village = list[0]?.village;
            const ini2 = n => (n || '?').trim().split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase();
            const avColors = ['#7c3aed','#0891b2','#15803d','#b45309','#be123c'];
            return (
              <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${M}25`, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: M, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  🏘️ {village ? `${village} से आज के समर्पण` : 'आपके गाँव से आज'}
                </div>
                {list.map((m, i) => {
                  const avColor = avColors[i % 5];
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: i < list.length - 1 ? 8 : 0, marginBottom: i < list.length - 1 ? 8 : 0, borderBottom: i < list.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      {m.photo
                        ? <img src={m.photo} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', flexShrink: 0, border: `1.5px solid ${avColor}40` }} />
                        : <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${avColor}15`, border: `2px solid ${avColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: avColor, flexShrink: 0 }}>{ini2(m.name)}</div>
                      }
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{m.name}</div>
                        {m.village && <div style={{ fontSize: 10, color: '#9ca3af' }}>📍 {m.village}</div>}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 20, padding: '3px 10px', flexShrink: 0 }}>
                        ✓ समर्पण किया{m.amount ? ` · ₹${Number(m.amount).toLocaleString('en-IN')}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

        </div>
      ))}

      {/* Load more */}
      {posts.length < postsTotal && (
        <button onClick={() => fetchPosts(postsPage + 1)} disabled={postsLoading}
          style={{ width: '100%', padding: '10px', background: '#f9fafb', border: '1px solid #e5e7eb',
            borderRadius: 10, fontSize: 12, fontWeight: 700, color: '#7c3aed',
            cursor: postsLoading ? 'not-allowed' : 'pointer', marginBottom: 14 }}>
          {postsLoading ? '...' : 'और पोस्ट देखें →'}
        </button>
      )}


      {/* ── Same Village Contributors Today ── */}
      <NewsDetailModal item={openedNews} onClose={() => setOpenedNews(null)} />

      {/* ── This month payment status ── */}
      {dash.payment_commitment === 'one_time' ? (
        /* One-Time payer — show a neutral info card, no monthly pressure */
        <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #d1d5db', padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                समर्पण प्रकार
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 20, padding: '3px 12px', fontSize: 12 }}>
                  एकमुश्त सदस्य
                </span>
              </div>
              {dash.this_month_paid ? (
                <div style={{ fontSize: 11, color: '#15803d', marginTop: 4 }}>
                  ✓ अंतिम भुगतान: ₹{Number(dash.this_month_amount).toLocaleString('en-IN')} · {dash.this_month_status === 'verified' ? 'Verified' : 'Pending'}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                  आपका समर्पण एकमुश्त आधार पर है — मासिक भुगतान आवश्यक नहीं
                </div>
              )}
            </div>
            <button onClick={() => onSwitchTab('payments')}
              style={{ background: '#f0f9ff', color: '#1d4ed8', border: '1.5px solid #bfdbfe', borderRadius: 10, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              भुगतान इतिहास →
            </button>
          </div>
        </div>
      ) : (
        /* Monthly payer — existing payment status card */
        <div style={{ background: '#fff', borderRadius: 14, border: dash.this_month_paid ? '1.5px solid #16a34a' : '1.5px solid #ef4444', padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                {new Date().toLocaleString('hi-IN', { month: 'long' })} माह का समर्पण
              </div>
              {dash.this_month_paid ? (
                <>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#15803d' }}>✓ ₹{Number(dash.this_month_amount).toLocaleString('en-IN')}</div>
                  <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2 }}>
                    {dash.this_month_status === 'verified' ? 'Verified ✓' : 'Received — Verification pending'}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#dc2626' }}>इस माह अभी भुगतान नहीं हुआ</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>मासिक राशि: ₹{Number(dash.announced_amount).toLocaleString('en-IN')}</div>
                  {!oneTimeReqSent ? (
                    <button
                      onClick={() => setOneTimeReqModal(true)}
                      style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, fontSize: 11, color: '#6b7280', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      क्या आपने एकमुश्त भुगतान किया है? अनुरोध करें
                    </button>
                  ) : (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#15803d', fontWeight: 600 }}>
                      ✓ अनुरोध भेजा जा चुका है — जल्द ही अपडेट होगा
                    </div>
                  )}
                </>
              )}
            </div>
            {!dash.this_month_paid && (
              <button onClick={() => onSwitchTab('payments')}
                style={{ background: M, color: G, border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                अभी देवें →
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── One-Time Payment Request Modal ── */}
      {oneTimeReqModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 340, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 28, textAlign: 'center', marginBottom: 8 }}>💰</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b', textAlign: 'center', marginBottom: 8 }}>
              एकमुश्त भुगतान अनुरोध
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginBottom: 20, lineHeight: 1.7 }}>
              यदि आपने पहले एकमुश्त (एक बार) भुगतान किया है और मासिक बकाया संदेश नहीं चाहते, तो नीचे अनुरोध भेजें।<br />
              <span style={{ color: '#15803d', fontWeight: 600 }}>Admin जल्द ही आपकी सदस्यता अपडेट करेंगे।</span>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setOneTimeReqModal(false)}
                style={{ flex: 1, background: '#f3f4f6', border: 'none', borderRadius: 10, padding: '11px 0', fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>
                रद्द करें
              </button>
              <button
                disabled={oneTimeReqSending}
                onClick={async () => {
                  setOneTimeReqSending(true);
                  try {
                    await axios.post(`${baseUrl}/member/inquiry`, {
                      inquiry_type: 'Payment Issue',
                      subject: 'एकमुश्त भुगतान प्रकार हेतु अनुरोध',
                      message: 'मेरा भुगतान एकमुश्त (One-Time) आधार पर है। कृपया मेरी सदस्यता को एकमुश्त में परिवर्तित करें ताकि मुझे मासिक बकाया संदेश न आएं।',
                    }, { headers: authHeader() });
                    setOneTimeReqSent(true);
                    setOneTimeReqModal(false);
                  } catch {
                    /* fail silently — modal stays open so user can retry */
                  } finally {
                    setOneTimeReqSending(false);
                  }
                }}
                style={{ flex: 1, background: M, border: 'none', borderRadius: 10, padding: '11px 0', fontSize: 13, fontWeight: 700, color: G, cursor: oneTimeReqSending ? 'not-allowed' : 'pointer', opacity: oneTimeReqSending ? 0.7 : 1 }}>
                {oneTimeReqSending ? 'भेज रहे हैं…' : 'अनुरोध भेजें ✓'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mission 21K Payment Due Banner ── */}
      {dash.mission_21k_visible !== false && dash.my_mission_status?.enrolled && dash.my_mission_status?.payment_due && (() => {
        const monthName = new Date().toLocaleString('hi-IN', { month: 'long', year: 'numeric' });
        return (
          <div style={{
            background: 'linear-gradient(135deg, #7c2d12 0%, #b45309 100%)',
            borderRadius: 14, padding: '14px 16px', marginBottom: 14,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: 12, border: '2px solid #fbbf24',
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#fde68a', marginBottom: 3 }}>
                🎯 Mission 21K — {monthName} समर्पण बाकी
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
                इस माह का ₹200 Mission 21K समर्पण अभी नहीं हुआ।
              </div>
            </div>
            <button
              onClick={() => onSwitchTab('payments')}
              style={{
                background: '#fde68a', color: '#92400e',
                border: 'none', borderRadius: 10,
                padding: '10px 18px', fontSize: 13, fontWeight: 900,
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              ₹200 Pay करें →
            </button>
          </div>
        );
      })()}

      {/* ── Streak card ── */}
      {dash.streak_months > 0 && (() => {
        const s = dash.streak_months;
        const nextLabel = s < 3 ? `${3-s} माह और → 🥉` : s < 6 ? `${6-s} माह और → 🥈` : s < 12 ? `${12-s} माह और → 🥇` : '🥇 Gold प्राप्त!';
        const nextPct   = s < 3 ? Math.round(s/3*100) : s < 6 ? Math.round(s/6*100) : s < 12 ? Math.round(s/12*100) : 100;
        const barColor  = s >= 12 ? '#d97706' : s >= 6 ? '#6b7280' : '#b45309';
        return (
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', borderLeft: '4px solid #d97706', padding: '12px 16px', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 28, lineHeight: 1 }}>🔥</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 900, color: '#111827' }}>{s} माह लगातार!</div>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{nextLabel}</div>
              </div>
              <button onClick={() => onSwitchTab('payments')} style={{ fontSize: 10, fontWeight: 700, color: M, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                जारी रखें →
              </button>
            </div>
            <div style={{ background: '#f3f4f6', borderRadius: 99, height: 5 }}>
              <div style={{ height: '100%', borderRadius: 99, background: '#d97706', width: `${nextPct}%`, transition: 'width 0.5s' }} />
            </div>
          </div>
        );
      })()}

      {/* ── Impact numbers ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'कुल समर्पण',  value: `₹${fmtAmount(dash.total_paid)}`,    sub: `${dash.payment_count} payments`, color: M },
          { label: 'माह भरे',     value: `${dash.total_months}`,               sub: 'Total months',                   color: '#1d4ed8' },
          { label: 'जोड़े सदस्य', value: `${dash.referrals}`,                  sub: 'Referred',                       color: '#15803d' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '12px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', marginTop: 3 }}>{s.label}</div>
            <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 1 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Mission 21K ── */}
      {dash.mission_21k_visible !== false && (() => {
        const MISSION_TARGET      = 21000;
        const MONTHLY_AMOUNT      = 200;
        const newRecruited        = dash.mission_new_members || 0;
        const totalRecords        = dash.total_members || 0;
        const pct                 = Math.min(100, Math.round((newRecruited / MISSION_TARGET) * 100));
        const remaining           = Math.max(0, MISSION_TARGET - newRecruited);
        const myMission           = dash.my_mission_status || {};
        const iEnrolled           = myMission.enrolled === true;
        const myContrib           = myMission.my_mission_referrals || dash.referrals || 0;
        const monthlyCollection   = newRecruited * MONTHLY_AMOUNT;
        const potentialCollection = MISSION_TARGET * MONTHLY_AMOUNT;
        const missionStart        = new Date('2026-09-25');
        const now                 = new Date();
        const missionNotStarted   = now < missionStart;

        return (
          <>
          <div style={{
            background: '#fff',
            borderRadius: 18, padding: '16px 16px 14px', marginBottom: 14,
            border: '1.5px solid #f3e8e8',
            boxShadow: '0 4px 20px rgba(122,0,0,0.08)',
          }}>
            {/* Top accent bar */}
            <div style={{ height: 4, borderRadius: 4, background: 'linear-gradient(90deg, #7a0000, #b45309, #fbbf24)', marginBottom: 14 }} />

            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 2 }}>
                  ABRSVS — सदस्यता अभियान
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: -0.5, lineHeight: 1.1, color: '#111827' }}>
                  Mission <span style={{ color: '#7a0000' }}>21K</span>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
                  नए • inactive • existing — कोई भी जुड़ सकता है
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 30, fontWeight: 900, color: '#7a0000', lineHeight: 1 }}>
                  {newRecruited.toLocaleString('en-IN')}
                </div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>जुड़े / 21,000</div>
              </div>
            </div>

            {/* Chips */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700, color: '#92400e' }}>
                ₹200/माह • fixed
              </span>
              <span style={{ background: '#f3f4f6', borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700, color: '#374151' }}>
                🗓 25 Sep 2026 से शुरू • जारी है
              </span>
              {missionNotStarted && (
                <span style={{ background: '#fef9c3', borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700, color: '#a16207' }}>
                  जल्द शुरू होगा
                </span>
              )}
            </div>

            {/* Progress bar */}
            <div style={{ height: 10, background: '#f3f4f6', borderRadius: 5, overflow: 'hidden', marginBottom: 5 }}>
              <div style={{ height: '100%', width: `${Math.max(pct, 0.5)}%`, background: 'linear-gradient(90deg, #7a0000, #b45309)', borderRadius: 5, transition: 'width 1.2s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>✓ {newRecruited.toLocaleString('en-IN')} सदस्य जुड़े</span>
              <span style={{ fontSize: 11, color: '#7a0000', fontWeight: 800 }}>{remaining.toLocaleString('en-IN')} और चाहिए!</span>
            </div>


            {/* Mission message */}
            {missionMsg && (
              <div style={{
                marginBottom: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                background: missionMsg.ok ? '#f0fdf4' : '#fef2f2',
                color: missionMsg.ok ? '#15803d' : '#b91c1c', border: `1px solid ${missionMsg.ok ? '#86efac' : '#fecaca'}`,
              }}>
                {missionMsg.text}
              </div>
            )}

            {/* Bottom CTA */}
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>
                  {iEnrolled ? '✓ आप Mission 21K में हैं' : 'आपका योगदान'}
                </div>
                <div style={{ fontSize: 14, fontWeight: 900, color: iEnrolled ? '#7a0000' : '#374151' }}>
                  {iEnrolled ? (myContrib > 0 ? `${myContrib} सदस्य जोड़े` : '₹200/माह enrolled') : 'अभी join नहीं किया'}
                </div>
              </div>
              {iEnrolled ? (
                <button onClick={() => onSwitchTab('referral')} style={{ background: '#7a0000', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                  सदस्य जोड़ें →
                </button>
              ) : (
                <button onClick={handleJoinMission21K} disabled={missionJoining} style={{ background: missionJoining ? '#e5e7eb' : '#7a0000', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 12, fontWeight: 800, cursor: missionJoining ? 'not-allowed' : 'pointer' }}>
                  {missionJoining ? 'जुड़ रहे हैं…' : '₹200/माह Join करें'}
                </button>
              )}
            </div>

            {/* Who can join */}
            <div style={{ marginTop: 10, display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[
                { label: '+ नया सदस्य', desc: 'Fresh join' },
                { label: '+ Inactive reactivate', desc: 'फिर से सक्रिय' },
                { label: '+ Existing opt-in', desc: 'मौजूदा जुड़ें' },
              ].map(item => (
                <div key={item.label} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#7a0000' }}>{item.label}</div>
                  <div style={{ fontSize: 8, color: '#9ca3af' }}>{item.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, textAlign: 'center', fontSize: 10, color: '#d1d5db' }}>
              campaign='mission_21k' • ₹200/माह fixed • {totalRecords.toLocaleString('en-IN')} total records
            </div>
          </div>
          </>
        );
      })()}

      {/* ── Community stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#15803d' }}>{Number(dash.active_members).toLocaleString('en-IN')}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginTop: 2 }}>सक्रिय सदस्य</div>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>Active members</div>
        </div>
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: '#1d4ed8' }}>+{dash.new_this_month}</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', marginTop: 2 }}>नए सदस्य</div>
          <div style={{ fontSize: 10, color: '#9ca3af' }}>This month</div>
        </div>
      </div>

      {/* ── My Village progress shortcut ── */}
      {profile?.perm_address?.village && (
        <div onClick={() => onSwitchTab('villages')} style={{
          background: 'linear-gradient(135deg,#fff5f5,#fef2f2)', border: `1.5px solid ${M}30`,
          borderRadius: 14, padding: '12px 16px', marginBottom: 10, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${M}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <EnvironmentOutlined style={{ fontSize: 20, color: M }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: M }}>
              {profile.perm_address.village} — {t('ग्राम प्रगति देखें', 'View Village Progress')}
            </div>
            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
              {t('सदस्य संख्या · भुगतान · जनगणना · रैंक', 'Members · Payments · Census · Rank')}
            </div>
          </div>
          <RiseOutlined style={{ fontSize: 18, color: M }} />
        </div>
      )}

      {/* ── Next upcoming event teaser ── */}
      {nextEvent && (() => {
        const isVirtual = nextEvent.event_type === 'virtual' || nextEvent.event_type === 'hybrid';
        const pm = PLATFORM_META[nextEvent.virtual_platform?.toLowerCase()] || {};
        const daysAway = Number(nextEvent.days_away);
        const isToday = daysAway === 0;
        const accentColor = isVirtual ? '#1d4ed8' : M;
        const title = (t('hi','en') === 'en' && nextEvent.title_en) ? nextEvent.title_en : nextEvent.title;
        return (
          <div
            onClick={() => onSwitchTab('sammelan')}
            style={{ background: `linear-gradient(135deg,${accentColor}12,${accentColor}06)`, border: `1.5px solid ${accentColor}35`, borderRadius: 14, padding: '12px 14px', marginBottom: 10, cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ fontSize: 26, flexShrink: 0 }}>{isVirtual ? (pm.icon || '🌐') : '📅'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: accentColor, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>
                  {isToday ? `🔔 ${t('आज का कार्यक्रम', "TODAY'S EVENT")}` : `🗓️ ${t('आगामी कार्यक्रम', 'UPCOMING EVENT')}`}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
                <div style={{ fontSize: 10, color: '#6b7280', marginTop: 3 }}>
                  {fmtEventDate(nextEvent.event_date, nextEvent.event_time, userTz)}
                </div>
              </div>
              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                {isVirtual && <div style={{ fontSize: 9, background: accentColor, color: '#fff', borderRadius: 20, padding: '2px 8px', fontWeight: 700, marginBottom: 4 }}>🌐 {t('ऑनलाइन', 'Online')}</div>}
                <div style={{ fontSize: 10, color: accentColor, fontWeight: 700 }}>{t('देखें →', 'View →')}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── City Community teaser (shown if member has current city set) ── */}
      {profile?.temp_address?.city && (
        <div onClick={() => onSwitchTab('city')} style={{
          background: '#fff5f5', border: `1px solid ${M}25`,
          borderRadius: 14, padding: '12px 16px', marginBottom: 10, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${M}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CompassOutlined style={{ fontSize: 20, color: M }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: M }}>
              {toTitle(profile.temp_address.city)} {t('में राजपुरोहित सदस्य', 'Rajpurohit Community')}
            </div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
              {t('अपने शहर के सदस्यों से जुड़ें →', 'Connect with members in your city →')}
            </div>
          </div>
          <div style={{ fontSize: 16, color: '#9ca3af' }}>›</div>
        </div>
      )}

      {/* ── Gram Pramukh card (shown if member's village has one) ── */}
      {gramPramukh && (
        <div style={{ background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1.5px solid #fbbf24', borderRadius: 14, padding: '12px 14px', marginBottom: 14, boxShadow: '0 2px 10px rgba(251,191,36,0.2)' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            👑 {t('आपके गाँव के ग्राम प्रमुख', 'Your Village Gram Pramukh')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {gramPramukh.photo
              ? <img src={gramPramukh.photo} alt={gramPramukh.name} style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: '2px solid #fbbf24', flexShrink: 0 }} />
              : <div style={{ width: 46, height: 46, borderRadius: '50%', background: '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, color: '#fef3c7', flexShrink: 0 }}>
                  {(gramPramukh.name || '?')[0]}
                </div>
            }
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: '#78350f' }}>{gramPramukh.name}</div>
              <div style={{ fontSize: 10, color: '#b45309', marginTop: 2 }}>
                {gramPramukh.village_name_hn || gramPramukh.village_name_en || t('गाँव प्रतिनिधि', 'Village Representative')}
              </div>
              {gramPramukh.notes && <div style={{ fontSize: 10, color: '#92400e', marginTop: 2, fontStyle: 'italic' }}>"{gramPramukh.notes}"</div>}
            </div>
            {gramPramukh.phone && (
              <a href={`tel:${gramPramukh.phone}`} style={{ background: '#92400e', color: '#fef3c7', border: 'none', borderRadius: 10, padding: '8px 12px', fontSize: 12, fontWeight: 800, textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                📞 {t('संपर्क', 'Call')}
              </a>
            )}
          </div>
        </div>
      )}

      {/* ── Expenditure transparency teaser ── */}
      {dash.fy_expenditure_total > 0 && (
        <div onClick={() => onSwitchTab('development')} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '12px 16px', marginBottom: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <RiseOutlined style={{ fontSize: 18, color: '#374151' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>
              {dash.fy_label} में ₹{Number(dash.fy_expenditure_total).toLocaleString('en-IN')} खर्च
            </div>
            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>
              {dash.fy_expenditure_count} कार्यों में — पूरा विवरण देखें →
            </div>
          </div>
          <span style={{ fontSize: 14, color: '#9ca3af' }}>›</span>
        </div>
      )}

      {/* impact card moved before posts feed */}

      {/* ── Post detail modal (opened from home) ── */}
      {openedPost && (
        <PostDetailModal
          post={openedPost}
          onClose={() => setOpenedPost(null)}
          myUserId={profile?.id}
        />
      )}

      {/* ── Village Leaderboard ── */}
      {leaderboard.filter(v => v.village && v.village !== 'null').length > 0 && (() => {
        const lb = leaderboard.filter(v => v.village && v.village !== 'null');
        const top3 = lb.slice(0, 3);
        const rest = lb.slice(3, 8);
        const medalCfg = [
          { icon: '🥇', bg: 'linear-gradient(145deg, #fffbeb, #fef3c7)', border: '#fbbf24', numColor: '#78350f', shadow: '0 4px 14px rgba(251,191,36,0.35)' },
          { icon: '🥈', bg: 'linear-gradient(145deg, #f8fafc, #e2e8f0)', border: '#94a3b8', numColor: '#374151', shadow: '0 4px 10px rgba(0,0,0,0.10)' },
          { icon: '🥉', bg: 'linear-gradient(145deg, #fff7ed, #fed7aa)', border: '#fb923c', numColor: '#7c2d12', shadow: '0 4px 10px rgba(249,115,22,0.25)' },
        ];
        return (
          <div style={{ marginBottom: 14, borderRadius: 18, overflow: 'hidden', boxShadow: '0 6px 24px rgba(0,0,0,0.12)' }}>
            {/* Header — warm saffron-maroon gradient */}
            <div style={{
              background: 'linear-gradient(135deg, #7c1d1d 0%, #b91c1c 50%, #ea580c 100%)',
              padding: '14px 16px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 900, color: '#fff', letterSpacing: 0.3 }}>🏆 {t('गाँव सक्रियता सूची', 'Village Activity Board')}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>{t('शीर्ष सक्रिय गाँव', 'Top Active Villages')}</div>
              </div>
              <div style={{ fontSize: 28, opacity: 0.9 }}>🏘️</div>
            </div>

            <div style={{ background: 'linear-gradient(180deg, #fff7ed 0%, #fff 40%)', padding: '14px 12px 10px' }}>
              {/* Top-3 medal cards */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
                {[top3[1], top3[0], top3[2]].filter(Boolean).map((v, idx) => {
                  const realIdx = [1, 0, 2][idx];
                  const cfg = medalCfg[realIdx];
                  const isFirst = realIdx === 0;
                  return (
                    <div key={realIdx} style={{
                      flex: isFirst ? 1.25 : 1,
                      background: cfg.bg,
                      border: `1.5px solid ${cfg.border}`,
                      borderRadius: 14,
                      padding: isFirst ? '14px 8px 12px' : '10px 8px 10px',
                      textAlign: 'center',
                      boxShadow: cfg.shadow,
                      order: [2, 1, 3][idx],
                    }}>
                      <div style={{ fontSize: isFirst ? 28 : 22, lineHeight: 1 }}>{cfg.icon}</div>
                      <div style={{
                        fontSize: isFirst ? 13 : 11, fontWeight: 800, color: '#111',
                        marginTop: 6, lineHeight: 1.25,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                      }}>{v.village}</div>
                      <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.district || ''}</div>
                      <div style={{
                        fontSize: isFirst ? 22 : 17, fontWeight: 900,
                        color: cfg.numColor, marginTop: 6,
                      }}>{v.member_count}</div>
                      <div style={{ fontSize: 8, color: '#9ca3af', marginTop: 1 }}>{t('सदस्य','Members')}</div>
                    </div>
                  );
                })}
              </div>

              {/* Rank 4–8 rows */}
              {rest.map((v, i) => {
                const pct = Math.min(100, (v.member_count / (lb[0]?.member_count || 1)) * 100);
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: '#fff',
                    borderRadius: 11,
                    padding: '8px 10px',
                    marginBottom: 6,
                    boxShadow: '0 1px 5px rgba(0,0,0,0.07)',
                    borderLeft: `3px solid ${M}`,
                  }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                      background: '#fef2f2', border: `1.5px solid ${M}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 900, color: M,
                    }}>{i + 4}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.village}</div>
                      <div style={{ fontSize: 9, color: '#9ca3af' }}>{v.district || ''}</div>
                      <div style={{ height: 3, background: '#f3f4f6', borderRadius: 2, marginTop: 5 }}>
                        <div style={{ height: '100%', borderRadius: 2, background: `linear-gradient(90deg, ${M}, #ea580c)`, width: `${pct}%`, transition: 'width 0.6s ease' }} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 900, color: M }}>{v.member_count}</div>
                      <div style={{ fontSize: 8, color: '#9ca3af' }}>{t('सदस्य','Members')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Village vs Village Competition ── */}
      {competition.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>🏆</span> {t('गाँव प्रतियोगिता', 'Village Competition')}
            <span style={{ fontSize: 9, background: '#fef3c7', color: '#92400e', borderRadius: 20, padding: '1px 7px', fontWeight: 700, marginLeft: 4 }}>
              {new Date().toLocaleString('hi-IN', { month: 'long' })}
            </span>
          </div>
          {competition.slice(0, 5).map((v, i) => (
            <div key={v.village} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', marginBottom: 4,
              background: i === 0 ? 'linear-gradient(135deg,#fffbeb,#fef3c7)' : '#fff',
              border: `1px solid ${i === 0 ? '#fcd34d' : '#f3f4f6'}`,
              borderRadius: 10, boxShadow: i === 0 ? '0 2px 8px rgba(252,211,77,0.3)' : 'none',
            }}>
              <div style={{ fontSize: i === 0 ? 20 : 14, fontWeight: 900, color: i === 0 ? '#d97706' : i === 1 ? '#9ca3af' : '#92400e', width: 24, textAlign: 'center', flexShrink: 0 }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}`}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#1c1e21', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.village}</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>{v.district} • {v.member_count} सदस्य</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: M }}>{v.payments_this_month} <span style={{ fontSize: 9, fontWeight: 400, color: '#9ca3af' }}>payments</span></div>
                {v.referrals_this_month > 0 && <div style={{ fontSize: 9, color: '#15803d', fontWeight: 700 }}>+{v.referrals_this_month} referral</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Member Milestones ── */}
      {milestones.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg,#fffbeb,#fef3c7)',
          borderRadius: 12, padding: '10px 14px', marginBottom: 14,
          border: '1.5px solid #fcd34d',
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            🌟 समर्पण वर्षगाँठ — ABRSVS में {milestones.length} सदस्य के {milestones[0].years} साल पूरे!
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
            {milestones.map(m => (
              <div key={m.id} style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                {m.photo
                  ? <img src={m.photo} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '2px solid #d97706' }} />
                  : <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900, color: '#fff' }}>{(m.name||'?')[0]}</div>
                }
                <div style={{ fontSize: 8, fontWeight: 700, color: '#92400e', textAlign: 'center', width: 44, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name?.split(' ')[0]}</div>
                <div style={{ fontSize: 7, background: '#d97706', color: '#fff', borderRadius: 20, padding: '1px 5px', fontWeight: 800 }}>{m.years} वर्ष</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Global Rajpurohit Community ── */}
      {countryStats && countryStats.abroad?.length > 0 && (() => {
        const myCountry   = profile?.temp_address?.country || null;
        const myCf        = countryFlag(myCountry);
        const isAbroad    = myCf?.isAbroad;
        const myRow       = myCountry ? countryStats.abroad.find(r =>
          r.country?.toLowerCase() === myCountry.toLowerCase()
        ) : null;
        const totalAbroad = countryStats.abroad.reduce((s, r) => s + (r.member_count || 0), 0);
        const visible     = countryExpanded ? countryStats.abroad : countryStats.abroad.slice(0, 5);
        return (
          <div style={{ marginBottom: 14, borderRadius: 14, overflow: 'hidden', boxShadow: '0 3px 14px rgba(0,0,0,0.09)', border: `1px solid ${M}20` }}>
            {/* Header — maroon, consistent with portal */}
            <div style={{ background: `linear-gradient(135deg, ${M} 0%, #b71c1c 100%)`, padding: '13px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <GlobalOutlined style={{ fontSize: 16 }} />
                  {t('वैश्विक राजपुरोहित समाज', 'Global Rajpurohit Community')}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', marginTop: 3 }}>
                  {totalAbroad} {t('सदस्य विदेश में', 'members abroad')} · {countryStats.abroad.length} {t('देश', 'countries')}
                </div>
              </div>
              <GlobalOutlined style={{ fontSize: 28, color: 'rgba(255,255,255,0.3)' }} />
            </div>

            <div style={{ background: '#fff8f8', padding: '12px 12px 8px' }}>
              {/* My country highlight */}
              {isAbroad && myRow && (
                <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, borderRadius: 12, padding: '11px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 32 }}>{myCf.flag}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 900, color: '#fff' }}>
                      {t('आपके देश में', 'In your country')} — {myRow.country}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                      {myRow.member_count} {t('राजपुरोहित सदस्य', 'Rajpurohit members')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 26, fontWeight: 900, color: G }}>{myRow.member_count}</div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)' }}>{t('सदस्य', 'members')}</div>
                  </div>
                </div>
              )}

              {/* Country list */}
              {visible.map((row, i) => {
                const cf   = countryFlag(row.country);
                const pct  = Math.min(100, (row.member_count / (countryStats.abroad[0]?.member_count || 1)) * 100);
                const isMe = myCountry && row.country?.toLowerCase() === myCountry.toLowerCase();
                return (
                  <div key={row.country} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: isMe ? '#fff5f5' : '#fff',
                    borderRadius: 10, padding: '9px 12px', marginBottom: 6,
                    border: isMe ? `1.5px solid ${M}` : '1px solid #f3f4f6',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                  }}>
                    {/* Flag */}
                    <div style={{ fontSize: 22, flexShrink: 0, width: 32, textAlign: 'center', lineHeight: 1 }}>
                      {cf?.flag || <GlobalOutlined style={{ color: '#d1d5db', fontSize: 18 }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{row.country}</div>
                        {isMe && <span style={{ fontSize: 9, background: M, color: '#fff', borderRadius: 20, padding: '1px 7px', fontWeight: 700 }}>YOU</span>}
                        <div style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 10, color: '#6b7280' }}>
                          <strong style={{ color: M, fontSize: 13 }}>{row.member_count}</strong> {t('सदस्य', 'members')}
                        </div>
                      </div>
                      <div style={{ height: 4, background: '#f3f4f6', borderRadius: 3, marginTop: 6 }}>
                        <div style={{ height: '100%', borderRadius: 3, background: `linear-gradient(90deg,${M},#b71c1c)`, width: `${pct}%`, transition: 'width 0.7s ease' }} />
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0, minWidth: 20, textAlign: 'right' }}>#{i + 1}</div>
                  </div>
                );
              })}

              {/* India row */}
              {countryStats.india_count > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 10, padding: '9px 12px', marginBottom: 6, border: '1px solid #f3f4f6' }}>
                  <div style={{ fontSize: 22, flexShrink: 0, width: 32, textAlign: 'center' }}>🇮🇳</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>India</div>
                    <div style={{ height: 4, background: '#f3f4f6', borderRadius: 3, marginTop: 6 }}>
                      <div style={{ height: '100%', borderRadius: 3, background: '#f97316', width: '100%' }} />
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, fontSize: 10, color: '#6b7280' }}>
                    <strong style={{ color: '#ea580c', fontSize: 13 }}>{countryStats.india_count}</strong> {t('सदस्य', 'members')}
                  </div>
                </div>
              )}

              {/* Show more / less */}
              {countryStats.abroad.length > 5 && (
                <button onClick={() => setCountryExpanded(e => !e)} style={{
                  width: '100%', background: 'none', border: `1px solid ${M}30`,
                  borderRadius: 8, padding: '6px 0', fontSize: 11, fontWeight: 700,
                  color: M, cursor: 'pointer', marginTop: 2,
                }}>
                  {countryExpanded
                    ? t('▲ कम दिखाएं', '▲ Show less')
                    : `▼ ${t('और', 'Show')} ${countryStats.abroad.length - 5} ${t('देश देखें', 'more countries')}`}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Payment due bottom sheet — shows once per session ── */}
      {payPrompt && dash && dash.payment_commitment !== 'one_time' && (
        <>
          {/* Backdrop */}
          <div onClick={dismissPayPrompt} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000 }} />
          {/* Sheet */}
          <div style={{
            position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1001,
            background: '#fff', borderRadius: '20px 20px 0 0',
            padding: '24px 20px 36px', boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
            animation: 'slideUp 0.3s ease',
          }}>
            {/* Handle */}
            <div style={{ width: 40, height: 4, background: '#e5e7eb', borderRadius: 2, margin: '0 auto 20px' }} />

            {/* Icon + heading */}
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              <div style={{ fontSize: 38, marginBottom: 8 }}>🙏</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: '#111827' }}>
                {t('इस माह का समर्पण बाकी है', 'Monthly Samarpan Pending')}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                {t(
                  `${new Date().toLocaleDateString('hi-IN', { month: 'long', year: 'numeric' })} का समर्पण अभी तक दर्ज नहीं हुआ`,
                  `${new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })} contribution not yet recorded`
                )}
              </div>
            </div>

            {/* Amount box */}
            <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 14, padding: '14px 16px', textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>{t('घोषित मासिक राशि', 'Declared Monthly Amount')}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: M }}>
                ₹{Number(dash.announced_amount || 0).toLocaleString('en-IN')}
              </div>
            </div>

            {/* Buttons */}
            <button onClick={goPayNow} style={{
              width: '100%', height: 50, background: M, color: '#fff',
              border: 'none', borderRadius: 14, fontSize: 15, fontWeight: 900,
              cursor: 'pointer', marginBottom: 10,
            }}>
              {t('अभी समर्पण करें →', 'Contribute Now →')}
            </button>
            <button onClick={dismissPayPrompt} style={{
              width: '100%', height: 44, background: '#f9fafb', color: '#6b7280',
              border: '1px solid #e5e7eb', borderRadius: 14, fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
            }}>
              {t('बाद में करूँगा / करूँगी', 'Remind me later')}
            </button>
          </div>
          <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        </>
      )}

      {/* ── Featured Donor Business Detail Modal ── */}
      {featuredBizOpen && dash?.featured_donor_business && (() => {
        const biz = dash.featured_donor_business;
        const bannerPhotos = biz.business_photo ? biz.business_photo.split(',').filter(Boolean) : [];
        const location = [biz.biz_village, biz.current_city, biz.district].filter(Boolean).join(', ');

        const sendRequest = async () => {
          setFeaturedBizStatus('sending');
          try {
            await axios.post(`${baseUrl}/member/biz-contact-request`, { entry_id: biz.biz_id }, { headers: authHeader() });
            setFeaturedBizStatus('sent');
          } catch {
            setFeaturedBizStatus('error');
          }
        };

        return (
          <>
            {/* Backdrop */}
            <div onClick={() => setFeaturedBizOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000 }} />
            {/* Modal */}
            <div style={{
              position: 'fixed', left: '50%', top: '50%', transform: 'translate(-50%, -50%)',
              width: '92%', maxWidth: 480, maxHeight: '88vh', overflowY: 'auto',
              background: '#fff', borderRadius: 18, zIndex: 2001,
              boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
            }}>
              {/* Modal header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid #f0f0f0' }}>
                <ShopOutlined style={{ color: M, fontSize: 16 }} />
                <span style={{ fontSize: 15, fontWeight: 800, color: M, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {biz.firm_name || biz.donor_name}
                </span>
                <button onClick={() => setFeaturedBizOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#6b7280', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>

              {/* Banner image */}
              {bannerPhotos.length > 0
                ? <img src={bannerPhotos[0]} alt="" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                : <div style={{ height: 80, background: `linear-gradient(135deg, ${M}12, ${M}05)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ShopOutlined style={{ fontSize: 32, color: `${M}40` }} />
                  </div>
              }

              {/* Thumbnail strip */}
              {bannerPhotos.length > 1 && (
                <div style={{ display: 'flex', gap: 6, padding: '8px 14px', overflowX: 'auto', scrollbarWidth: 'none' }}>
                  {bannerPhotos.map((ph, i) => (
                    <img key={i} src={ph} alt="" style={{ width: 52, height: 52, borderRadius: 8, objectFit: 'cover', flexShrink: 0, border: i === 0 ? `2px solid ${M}` : '1.5px solid #e5e7eb' }} />
                  ))}
                </div>
              )}

              <div style={{ padding: '14px 16px 20px' }}>
                {/* Owner row + category chips */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  {(biz.biz_owner_photo || biz.donor_photo)
                    ? <img src={biz.biz_owner_photo || biz.donor_photo} alt="" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${M}30`, flexShrink: 0 }} />
                    : <div style={{ width: 52, height: 52, borderRadius: '50%', background: `${M}15`, border: `2px solid ${M}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: M, flexShrink: 0 }}>
                        {(biz.donor_name || '?').trim().split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase()}
                      </div>
                  }
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 4 }}>{biz.donor_name}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {biz.sector_name && <span style={{ fontSize: 10, fontWeight: 700, color: M, background: `${M}12`, borderRadius: 20, padding: '3px 10px' }}>{biz.sector_name}</span>}
                      {biz.sub_sector_label && <span style={{ fontSize: 10, color: '#5b21b6', background: '#ede9fe', borderRadius: 20, padding: '3px 10px' }}>{biz.sub_sector_label}</span>}
                    </div>
                  </div>
                </div>

                {/* Detail rows */}
                <div style={{ borderRadius: 12, border: '1px solid #f0f0f0', overflow: 'hidden', marginBottom: 12 }}>
                  {[
                    { icon: <ShopOutlined />, label: 'फर्म / दुकान', value: biz.firm_name },
                    { icon: '📅', label: 'स्थापना वर्ष', value: biz.establishment_year },
                    { icon: '🏅', label: 'अनुभव', value: biz.experience_years ? `${biz.experience_years} वर्ष` : null },
                    { icon: '⭐', label: 'रैंक / पदवी', value: biz.biz_rank },
                    { icon: '💼', label: 'पद / Post', value: biz.post },
                    { icon: '📍', label: 'स्थान', value: location },
                  ].filter(r => r.value).map((r, i, arr) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: i < arr.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                      <span style={{ fontSize: 16, color: M, width: 20, textAlign: 'center', flexShrink: 0 }}>{r.icon}</span>
                      <span style={{ fontSize: 12, color: '#6b7280', width: 80, flexShrink: 0 }}>{r.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{r.value}</span>
                    </div>
                  ))}
                </div>

                {/* Business about */}
                {biz.business_about && (
                  <div style={{ background: '#f9fafb', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 12 }}>
                    {biz.business_about}
                  </div>
                )}

                {/* Contact note */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 14, padding: '10px 12px', background: '#fef9c3', borderRadius: 10, border: '1px solid #fde047' }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>🔒</span>
                  <span style={{ fontSize: 11, color: '#713f12', lineHeight: 1.6 }}>
                    संपर्क नंबर देखने के लिए अनुरोध भेजें। स्वामी के स्वीकार करने पर दोनों को नंबर दिखेगा।
                  </span>
                </div>

                {/* Contact request button */}
                {featuredBizStatus === 'sent'
                  ? <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 13, fontWeight: 700, color: '#15803d', background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
                      ✅ अनुरोध भेज दिया गया!
                    </div>
                  : <button
                      onClick={sendRequest}
                      disabled={featuredBizStatus === 'sending'}
                      style={{
                        width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
                        background: featuredBizStatus === 'sending' ? '#9ca3af' : M,
                        color: G, fontSize: 14, fontWeight: 800, cursor: featuredBizStatus === 'sending' ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}>
                      <PhoneOutlined />
                      {featuredBizStatus === 'sending' ? 'भेजा जा रहा है…' : 'संपर्क नंबर के लिए अनुरोध भेजें'}
                    </button>
                }
                {featuredBizStatus === 'error' && (
                  <div style={{ textAlign: 'center', fontSize: 11, color: '#dc2626', marginTop: 8 }}>कुछ गड़बड़ हुई, दोबारा try करें</div>
                )}
              </div>
            </div>
          </>
        );
      })()}

      <WritePostModal
        open={writeOpen}
        onClose={() => setWriteOpen(false)}
        isActiveUser={profile?.is_active === true}
        onPosted={() => {
          setWriteOpen(false);
          fetchPosts(1);
        }}
      />

      {feedOpenPost && (
        <PostDetailModal
          post={feedOpenPost}
          onClose={() => setFeedOpenPost(null)}
          myUserId={profile?.id}
          onDelete={id => { setPosts(prev => prev.filter(x => x.id !== id)); setFeedOpenPost(null); }}
        />
      )}

    </div>
  );
}

// ── Samaj Connect Widget — pending requests + people you may know ─────────────
function SamajConnectWidget({ tabBadges, profile, onSwitchTab, onOpenProfile }) {
  const [suggestions,  setSuggestions]  = useState(null);
  const [sending,      setSending]      = useState({});
  const [loadingMore,  setLoadingMore]  = useState({});
  const [pages,        setPages]        = useState({ gotra: 1, village: 1, nearby: 1, other: 1 });
  const [hasMore,      setHasMore]      = useState({ gotra: true, village: true, nearby: true, other: true });
  const [extra,        setExtra]        = useState({ gotra: [], village: [], nearby: [], other: [] });
  const [search,       setSearch]       = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = not searching
  const [searchLoading, setSearchLoading] = useState(false);
  const scrollRef    = useRef(null); // must be at top — before any early returns
  const searchTimer  = useRef(null);

  const fetchSuggestions = useCallback(() => {
    setSuggestions(null);
    setExtra({ gotra: [], village: [], nearby: [], other: [] });
    setPages({ gotra: 1, village: 1, nearby: 1, other: 1 });
    setHasMore({ gotra: true, village: true, nearby: true, other: true });
    axios.get(`${baseUrl}/member/suggestions`, { headers: authHeader() })
      .then(r => { if (r.data.status) setSuggestions(r.data); })
      .catch(() => setSuggestions({ gotra_suggestions: [], village_suggestions: [], nearby_suggestions: [], other_suggestions: [] }));
  }, []); // eslint-disable-line

  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const doSearch = async (q) => {
    if (!q.trim()) { setSearchResults(null); setSearchLoading(false); return; }
    setSearchLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/member-search?search=${encodeURIComponent(q)}&limit=30`, { headers: authHeader() });
      if (r.data.status) setSearchResults(r.data.result || []);
    } catch {} finally { setSearchLoading(false); }
  };

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    if (!val.trim()) { setSearchResults(null); return; }
    searchTimer.current = setTimeout(() => doSearch(val), 400);
  };

  const sendConnect = async (id, e) => {
    e.stopPropagation();
    if (sending[id]) return;
    setSending(s => ({ ...s, [id]: true }));
    try {
      await axios.post(`${baseUrl}/member/contact-request`, { to_user_id: id }, { headers: authHeader() });
      notification.success({ message: '🤝 संपर्क अनुरोध भेजा गया!' });
      setSending(s => ({ ...s, [id]: 'done' }));
    } catch (err) {
      notification.warning({ message: err.response?.data?.message || 'Error' });
      setSending(s => ({ ...s, [id]: false }));
    }
  };

  const loadMore = async (group) => {
    if (loadingMore[group]) return;
    const nextPage = pages[group] + 1;
    // Remember scroll position before adding items
    const scrollEl = scrollRef.current;
    const scrollBefore = scrollEl ? scrollEl.scrollLeft : 0;
    const widthBefore  = scrollEl ? scrollEl.scrollWidth : 0;
    setLoadingMore(l => ({ ...l, [group]: true }));
    try {
      const sq = search ? `&search=${encodeURIComponent(search)}` : '';
      const r = await axios.get(`${baseUrl}/member/suggestions/more?group=${group}&page=${nextPage}${sq}`, { headers: authHeader() });
      if (r.data.status) {
        setExtra(e => ({ ...e, [group]: [...e[group], ...(r.data.result || [])] }));
        setHasMore(h => ({ ...h, [group]: r.data.has_more }));
        setPages(p => ({ ...p, [group]: nextPage }));
        // After state update renders, smooth-scroll to where new items start
        setTimeout(() => {
          if (scrollEl) {
            const addedWidth = scrollEl.scrollWidth - widthBefore;
            scrollEl.scrollTo({ left: scrollBefore + addedWidth - 96, behavior: 'smooth' });
          }
        }, 80);
      }
    } catch {} finally { setLoadingMore(l => ({ ...l, [group]: false })); }
  };

  const pendingCount = tabBadges?.contacts || 0;
  const ini = n => (n || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const gotraSugg   = [...(suggestions?.gotra_suggestions   || []), ...extra.gotra];
  const villageSugg = [...(suggestions?.village_suggestions || []), ...extra.village];
  const nearbySugg  = [...(suggestions?.nearby_suggestions  || []), ...extra.nearby];
  const otherSugg   = [...(suggestions?.other_suggestions   || []), ...extra.other];

  // deduplicate across groups
  const seenIds = new Set();
  const dedup = arr => arr.filter(m => { if (seenIds.has(m.id)) return false; seenIds.add(m.id); return true; });
  const gotraList   = dedup(gotraSugg);
  const villageList = dedup(villageSugg);
  const nearbyList  = dedup(nearbySugg);
  const otherList   = dedup(otherSugg);

  const hasSugg = gotraList.length > 0 || villageList.length > 0 || nearbyList.length > 0 || otherList.length > 0;
  if (pendingCount === 0 && !hasSugg && suggestions !== null) return null;

  // Fixed-height card so all cards align perfectly regardless of content
  const MemberCard = ({ m }) => {
    const location = [m.village, m.district].filter(Boolean).join(', ');
    return (
      <div style={{
        flexShrink: 0, width: 86,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        {/* Photo */}
        <div onClick={() => onOpenProfile?.(m.id)} style={{
          width: 56, height: 56, borderRadius: '50%', overflow: 'hidden',
          marginBottom: 6, border: `2px solid ${M}30`, cursor: 'pointer', flexShrink: 0,
        }}>
          {m.photo
            ? <img src={m.photo} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
            : <div style={{ width: '100%', height: '100%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: G }}>{ini(m.name)}</div>}
        </div>
        {/* Fixed-height info block — all cards same height */}
        <div onClick={() => onOpenProfile?.(m.id)} style={{ width: '100%', cursor: 'pointer', flex: 1 }}>
          {/* Name — 2 lines max */}
          <div style={{ fontSize: 10, fontWeight: 800, color: '#111827', lineHeight: 1.3, marginBottom: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 26, textAlign: 'center' }}>
            {m.name}
          </div>
          {/* Father — 1 line */}
          <div style={{ fontSize: 9, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center', minHeight: 13 }}>
            {m.father_name ? `पि. ${m.father_name}` : ''}
          </div>
          {/* Gotra — 1 line */}
          <div style={{ fontSize: 9, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center', minHeight: 13 }}>
            {m.gotra || ''}
          </div>
          {/* Location — 1 line */}
          <div style={{ fontSize: 9, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center', minHeight: 13, marginBottom: 6 }}>
            {location ? <><EnvironmentOutlined style={{ fontSize: 8 }} /> {location}</> : ''}
          </div>
        </div>
        {/* Connect button — always at bottom */}
        <button disabled={!!sending[m.id]} onClick={(e) => sendConnect(m.id, e)} style={{
          fontSize: 9, fontWeight: 700, width: '100%',
          color: sending[m.id] === 'done' ? '#15803d' : M,
          background: sending[m.id] === 'done' ? '#dcfce7' : '#fff5f5',
          border: `1px solid ${sending[m.id] === 'done' ? '#86efac' : M}`,
          borderRadius: 6, padding: '4px 4px', cursor: sending[m.id] ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
        }}>
          {sending[m.id] === 'done'
            ? <><CheckCircleOutlined style={{ fontSize: 9 }} /> भेजा</>
            : sending[m.id] ? '...'
            : <><UserAddOutlined style={{ fontSize: 9 }} /> जोड़ें</>}
        </button>
      </div>
    );
  };

  const SectionBlock = ({ list, group, label, icon, color }) => {
    if (list.length === 0) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          <span style={{ fontSize: 12 }}>{icon}</span> {label}
        </div>
        {/* Horizontal scroll strip */}
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'none' }}>
          {list.map(m => <MemberCard key={m.id} m={m} />)}
          {/* Load More card at the end of the scroll */}
          {hasMore[group] && (
            <div style={{ flexShrink: 0, width: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <button onClick={() => loadMore(group)} disabled={loadingMore[group]} style={{
                width: 56, height: 56, borderRadius: '50%', border: `2px dashed ${color}60`,
                background: 'none', cursor: loadingMore[group] ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color, fontSize: 18, marginBottom: 5,
              }}>
                {loadingMore[group] ? <LoadingOutlined style={{ fontSize: 16 }} /> : <PlusOutlined style={{ fontSize: 16 }} />}
              </button>
              <div style={{ fontSize: 9, fontWeight: 700, color, textAlign: 'center' }}>
                {loadingMore[group] ? 'लोड…' : 'और देखें'}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '14px 16px', marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <LinkOutlined style={{ color: M, fontSize: 14 }} /> Samaj Connect
      </div>

      {/* Pending requests */}
      {pendingCount > 0 && (
        <div onClick={() => onSwitchTab('contacts')} style={{
          background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', border: '1.5px solid #bfdbfe',
          borderRadius: 10, padding: '10px 14px', marginBottom: 14,
          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
        }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MailOutlined style={{ color: '#fff', fontSize: 16 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#1e40af' }}>{pendingCount} नए संपर्क अनुरोध हैं</div>
            <div style={{ fontSize: 10, color: '#3730a3', marginTop: 1 }}>स्वीकार करने पर मोबाइल नंबर दिखेगा</div>
          </div>
          <RightOutlined style={{ fontSize: 13, color: '#1d4ed8' }} />
        </div>
      )}

      {/* Search box */}
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <SearchOutlined style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 13, zIndex: 1 }} />
        <input
          value={search}
          onChange={e => handleSearch(e.target.value)}
          placeholder="नाम, गाँव, जिला, गोत्र, व्यवसाय…"
          style={{
            width: '100%', boxSizing: 'border-box',
            paddingLeft: 32, paddingRight: search ? 28 : 10,
            height: 34, border: '1px solid #e5e7eb', borderRadius: 8,
            fontSize: 12, outline: 'none', color: '#374151',
          }}
        />
        {search && (
          <button onClick={() => handleSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
        )}
      </div>

      {/* ── SEARCH MODE — universal results ── */}
      {search.trim() ? (
        searchLoading
          ? <div style={{ textAlign: 'center', padding: 20 }}><LoadingOutlined /></div>
          : searchResults && searchResults.length === 0
            ? <div style={{ textAlign: 'center', padding: 16, color: '#9ca3af', fontSize: 12 }}>कोई सदस्य नहीं मिला</div>
            : searchResults && (
                <div ref={scrollRef} style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'none', alignItems: 'flex-start' }}>
                  {searchResults.map(m => <MemberCard key={m.id} m={{ ...m, connection_status: m.connection_status }} />)}
                </div>
              )
      ) : (
        <>
          {suggestions === null && <div style={{ textAlign: 'center', padding: 20 }}><LoadingOutlined /></div>}

          {/* All suggestions — single unified horizontal scroll */}
          {hasSugg && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 4 }}>
            <BulbOutlined style={{ fontSize: 11 }} /> लोग जो आप जान सकते हैं
          </div>
          <div
            ref={scrollRef}
            style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6, scrollbarWidth: 'none', alignItems: 'flex-start' }}>
            {[...gotraList, ...villageList, ...nearbyList, ...otherList].map(m => (
              <MemberCard key={m.id} m={m} />
            ))}
            {/* Load more button at end of scroll */}
            {(hasMore.gotra || hasMore.village || hasMore.nearby || hasMore.other) && (
              <div style={{ flexShrink: 0, width: 72, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8 }}>
                <button
                  onClick={() => {
                    if (hasMore.gotra)   loadMore('gotra');
                    else if (hasMore.village) loadMore('village');
                    else if (hasMore.nearby)  loadMore('nearby');
                    else if (hasMore.other)   loadMore('other');
                  }}
                  disabled={Object.values(loadingMore).some(Boolean)}
                  style={{
                    width: 56, height: 56, borderRadius: '50%',
                    border: `2px dashed ${M}60`, background: `${M}08`,
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: M, marginBottom: 5,
                  }}>
                  {Object.values(loadingMore).some(Boolean)
                    ? <LoadingOutlined style={{ fontSize: 16 }} />
                    : <PlusOutlined style={{ fontSize: 16 }} />}
                </button>
                <div style={{ fontSize: 9, fontWeight: 700, color: M, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {Object.values(loadingMore).some(Boolean) ? 'लोड…' : 'और देखें'}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {hasSugg && (
        <button onClick={() => onSwitchTab('directory')} style={{ marginTop: 4, width: '100%', background: 'none', border: `1px solid ${M}30`, borderRadius: 8, padding: '7px 0', fontSize: 11, fontWeight: 700, color: M, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
          <SearchOutlined style={{ fontSize: 11 }} /> सभी सदस्य देखें
        </button>
      )}
        </>
      )}
    </div>
  );
}

// ── Dropdown options ──────────────────────────────────────────────────────────
